
import { Head, usePage, Link, useForm, router } from '@inertiajs/react'
import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import './Dashboard.css'
import React from 'react'
import { cableConsumer } from '../../lib/cable'

type DashboardTab =
  | 'blasts'
  | 'blast_details'
  | 'inbox'
  | 'campaigns'
  | 'templates'
  | 'leads'
  | 'organizations'
  | 'organization_details'

type DashboardCampaign = {
  id: number
  name?: string
  displayName: string
  nickname?: string | null
  source?: string | null
  source_id?: string | null
  status?: string | null
  description?: string | null
  createdAt?: string
  updatedAt?: string
  archivedAt?: string | null
  fromNumbers: string[]
  external_metadata?: {
    status?: string
    description?: string
    brandId?: string
    cspId?: string
    usecase?: string
    createDate?: string
    optinKeywords?: string
    optoutKeywords?: string
    helpKeywords?: string
  } | null
}

type DashboardTemplate = {
  id: number
  name: string
  body: string
  campaignId?: number | null
}

type DashboardZipCode = {
  id: number
  code: string
  city: string
  state: string
  propertyCount?: number | null
}

type DashboardOrganization = {
  id: number
  name: string
  company?: string
  industry?: string | null
  icon?: string
  status?: string
  campaigns: DashboardCampaign[]
  archivedCampaigns?: DashboardCampaign[]
  templates: DashboardTemplate[]
  zipCodes: DashboardZipCode[]
}

type OrganizationSummary = {
  id: number
  name: string
  status?: string
  industry?: string | null
  campaignsCount: number
  leadsCount: number
  createdAt: string
}

type OrganizationOption = {
  id: number
  name: string
}

type DashboardBlast = {
  id: number
  name: string
  organizationId: number
  status: string
  zipCode: string
  sent: number
  totalContacts: number
  replies: number
  date: string
  campaignName: string
  templateName?: string
  followUpTemplateName?: string
  createdAtLabel: string
  fromNumber?: string
  messageVolume?: number
  workingHoursLabel: string
  workingDaysLabel: string
  followUpDelayLabel: string
  startDatetimeLabel: string
  isFollowUpBlast?: boolean
}

type DashboardMessage = {
  id: number | string
  dir: 'in' | 'out'
  text: string
  // Present on optimistic sends and on messages pushed over InboxChannel; the
  // dashboard's own serializer omits it.
  createdAt?: string
}

type DashboardLead = {
  id: number
  name: string
  address: string
  phone: string
  zip?: string
  source?: string
  status: string
  intent: string
  hasUnreadMessages?: boolean
  lastActivity: string
  updatedAt: string
  updatedAtLabel: string
  organizationId?: number
  messages?: DashboardMessage[]
}

type EditableOrganization = {
  id: number
  name: string
  status?: string
  industry?: string | null
}

type DashboardUser = {
  email: string
  is_super_admin?: boolean
}

type DashboardFlash = {
  new_organization_id?: number | null
}

type DashboardPageProps = {
  user?: DashboardUser | null
  customers?: unknown[]
  blasts?: DashboardBlast[]
  leads?: DashboardLead[]
  leadStatuses?: string[]
  activeTab?: DashboardTab
  organizationId?: number | null
  organizations?: OrganizationSummary[]
  global_organizations?: DashboardOrganization[]
  active_organization?: DashboardOrganization | null
  active_blast?: DashboardBlast | null
  flash?: DashboardFlash
}

type SidebarProps = {
  adminName: string
  adminRole: string
  activeTab: string
  isSuperAdmin?: boolean
}

type TopbarProps = {
  organizations: DashboardOrganization[]
  activeOrganization: DashboardOrganization
  onChangeOrganization: (value: string) => void
}

type NewTemplateModalProps = {
  onClose: () => void
  currentOrganization: DashboardOrganization
}

type EditTemplateModalProps = {
  template: DashboardTemplate
  onClose: () => void
  currentOrganization: DashboardOrganization
  allOrganizations: OrganizationOption[]
}

type LaunchBlastPayload = {
  name: string
  zipCode: string
  startDatetime: string | null
  workingHoursStart: number
  workingHoursEnd: number
  workingDays: string[]
  messageVolume: number
  fromNumber: string
  campaignId?: number
  templateId?: number
  followUpDelayHours: number
  followUpTemplateId: number | null
  organizationId: number
  testPhoneNumbers?: string
  reuseExistingLeads?: boolean
  contactLimit?: string
}

type NewBlastModalProps = {
  onClose: () => void
  onLaunch: (payload: LaunchBlastPayload) => void
  organization: DashboardOrganization
}

type BlastsViewProps = {
  blasts: DashboardBlast[]
  onNewBlast: () => void
  organization: DashboardOrganization
}

type BlastDetailsViewProps = {
  blast: DashboardBlast | null
}

// `leads` is a server prop, so its updater is Inertia's client-side prop
// replacement rather than a React state setter — same call shape, different
// owner. See the setLeads definition in App.
type LeadsUpdater = (updater: DashboardLead[] | ((current: DashboardLead[]) => DashboardLead[])) => void

type InboxViewProps = {
  leads: DashboardLead[]
  setLeads: LeadsUpdater
  organization: DashboardOrganization
}

type ViewContactModalProps = {
  lead: DashboardLead | null
  onClose: () => void
}

type LeadsViewProps = {
  leads: DashboardLead[]
  leadStatuses: string[]
  organization: DashboardOrganization
}

type ViewCampaignModalProps = {
  campaign: DashboardCampaign | null
  onClose: () => void
}

type NewCampaignModalProps = {
  onClose: () => void
  currentOrganization: DashboardOrganization
}

type NewOrganizationModalProps = {
  onClose: () => void
}

type EditOrganizationModalProps = {
  organization: EditableOrganization
  onClose: () => void
}

type OrganizationsViewProps = {
  organizations: OrganizationSummary[]
  activeOrganizationId: number | null
  onNew: () => void
  onSwitch: (id: number) => void
  onEdit: (org: EditableOrganization) => void
}

type AddZipcodeModalProps = {
  organization: DashboardOrganization
  existingZipCodeIds: number[]
  onClose: () => void
}

type MetricCardStyle = React.CSSProperties & {
  '--accent-1': string
}


// Module-level frozen empties. Falling back to a fresh `[]` inside render
// hands every child a new array identity on every render, which defeats
// memoisation and re-triggers effects that depend on the list.
const EMPTY_BLASTS: DashboardBlast[] = []
const EMPTY_LEADS: DashboardLead[] = []

// --- COMPONENTS ---

// 1. Sidebar
const Sidebar = ({ adminName, adminRole, activeTab, isSuperAdmin, unreadCount }: SidebarProps & { unreadCount?: number }) => (
  <div className="sidebar">
    <div className="sidebar-header">
      <div className="logo-icon"><i className="fa-solid fa-bolt"></i></div>
      <div style={{ fontWeight: 700, fontSize: '1.2rem', letterSpacing: '-0.02em', background: 'linear-gradient(90deg, #fff, #94a3b8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
        FillMyCalendar
      </div>
    </div>
    <div className="sidebar-nav">
      <Link href="/blasts" className={`nav-item ${activeTab === 'blasts' ? 'active' : ''}`} style={{ display: 'block', textDecoration: 'none' }}>
        <i className="fa-solid fa-rocket" style={{ width: 20 }}></i> Prospecting Blasts
      </Link>
      <Link href="/inbox" className={`nav-item ${activeTab === 'inbox' ? 'active' : ''}`} style={{ textDecoration: 'none', display: 'flex', justifyContent: 'space-between' }}>
        <span><i className="fa-solid fa-inbox" style={{ width: 20 }}></i> Lead Inbox</span>
        {unreadCount ? <span className="badge badge-danger" style={{ padding: '0 6px' }}>{unreadCount}</span> : null}
      </Link>
      <Link href="/campaigns" className={`nav-item ${activeTab === 'campaigns' ? 'active' : ''}`} style={{ display: 'block', textDecoration: 'none' }}>
        <i className="fa-solid fa-bullseye" style={{ width: 20 }}></i> Campaigns
      </Link>
      <Link href="/templates" className={`nav-item ${activeTab === 'templates' ? 'active' : ''}`} style={{ display: 'block', textDecoration: 'none' }}>
        <i className="fa-solid fa-file-lines" style={{ width: 20 }}></i> Templates
      </Link>
      <Link href="/leads" className={`nav-item ${activeTab === 'leads' ? 'active' : ''}`} style={{ display: 'block', textDecoration: 'none' }}>
        <i className="fa-solid fa-users" style={{ width: 20 }}></i> Leads
      </Link>

      <Link href="/organizations" className={`nav-item ${activeTab === 'organizations' ? 'active' : ''}`} style={{ display: 'block', textDecoration: 'none' }}>
        <i className="fa-solid fa-sitemap" style={{ width: 20 }}></i> Organizations
      </Link>
      
      {isSuperAdmin && (
        <Link href="/super_admin/dashboard" className="nav-item" style={{ display: 'block', textDecoration: 'none', color: 'var(--accent-2)' }}>
          <i className="fa-solid fa-lock" style={{ width: 20 }}></i> Super Admin
        </Link>
      )}
    </div>
    <div className="user-profile">
      <div className="avatar">{adminName.substring(0, 2).toUpperCase()}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '0.9rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{adminName}</div>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-3)' }}>{adminRole}</div>
        <Link
          href="/users/sign_out"
          method="delete"
          as="button"
          className="btn btn-secondary"
          style={{ marginTop: 8, padding: '4px 10px', fontSize: '0.75rem', width: '100%', justifyContent: 'center' }}
        >
          <i className="fa-solid fa-right-from-bracket"></i> Sign Out
        </Link>
      </div>
    </div>
  </div>
);

// 2. Topbar Context Switcher
const Topbar = ({ organizations, activeOrganization, onChangeOrganization }: TopbarProps) => (
  <div className="topbar glass-panel" style={{ borderRadius: 0, borderTop: 0, borderLeft: 0, borderRight: 0 }}>
    <div className="context-switch">
      <span style={{ color: 'var(--text-2)', fontSize: '0.85rem' }}>Acting on behalf of:</span>
      <div style={{ width: 24, height: 24, borderRadius: 4, background: 'var(--accent-1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, flexShrink: 0 }}>
        <i className={`fa-solid ${activeOrganization.icon || 'fa-sitemap'}`}></i>
      </div>
      <div className="context-switch-control">
        <select
          className="context-switch-select"
          value={activeOrganization.id || ''}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onChangeOrganization(e.target.value)}
        >
          {organizations.map((c) => (
            <option key={c.id} value={c.id} style={{ background: 'var(--bg-base)' }}>{c.company}</option>
          ))}
        </select>
      </div>
    </div>
  </div>
);

const TemplateVariablesHint = () => (
  <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginTop: 8 }}>
    Available variables: {'{{.ID}}'}, {'{{.Name}}'}, {'{{.Phone}}'}, {'{{.Address}}'}, {'{{.City}}'}, {'{{.State}}'}, {'{{.Zip}}'}.
    {' '}City is the property’s city. Missing location fields are blank.
  </div>
);

// 3. New Template Modal
const NewTemplateModal = ({ onClose, currentOrganization }: NewTemplateModalProps) => {
  // Archived campaigns are retired from new work everywhere - never offer
  // one as a link target for a brand new template.
  const activeCampaigns = (currentOrganization.campaigns || []).filter(c => !c.archivedAt);

  const { data, setData, post, processing, reset } = useForm({
    name: '',
    body: '',
    campaign_id: activeCampaigns.length > 0 ? activeCampaigns[0].id : '',
    organization_id: currentOrganization.id
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    post('/templates', {
      onSuccess: () => {
        reset();
        onClose();
      }
    });
  };

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal-content glass-panel" style={{ maxWidth: 500 }}>
        <div className="modal-header">
          <h3 style={{ fontSize: '1.25rem', fontWeight: 600 }}>Create New Template</h3>
          <button className="btn-icon" onClick={onClose}><i className="fa-solid fa-xmark"></i></button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Template Name</label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. Initial Outreach"
                value={data.name}
                onChange={e => setData('name', e.target.value)}
                required
              />
            </div>

            {activeCampaigns.length > 0 && (
              <div className="form-group">
                <label className="form-label">Link to Campaign (Optional)</label>
                <select
                  className="form-control"
                  value={data.campaign_id}
                  onChange={e => setData('campaign_id', e.target.value)}
                >
                  <option value="">-- No Campaign --</option>
                  {activeCampaigns.map((c) => (
                    <option key={c.id} value={c.id}>{c.displayName}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Message Body</label>
              <textarea
                className="form-control"
                rows={4}
                placeholder="Hi {{.Name}}, we're reaching out to homeowners in {{.City}}."
                value={data.body}
                onChange={e => setData('body', e.target.value)}
                required
              />
              <TemplateVariablesHint />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={processing || !data.name || !data.body}>
              {processing ? 'Saving...' : 'Save Template'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// 3.5 Edit Template Modal
const EditTemplateModal = ({ template, onClose, currentOrganization, allOrganizations }: EditTemplateModalProps) => {
  // currentOrganization.campaigns is active-only by design (the backend's
  // default scope excludes archived campaigns from normal payloads) - don't
  // offer one as a NEW pick. But if this template is already linked to one
  // (archived after the fact), it won't be in that array at all anymore, so
  // pull it from archivedCampaigns specifically and keep it visible - the
  // current assignment shouldn't be silently hidden or dropped.
  const linkedArchivedCampaign = (currentOrganization.archivedCampaigns || []).find(c => c.id === template.campaignId);
  const selectableCampaigns = linkedArchivedCampaign
    ? [ ...(currentOrganization.campaigns || []), linkedArchivedCampaign ]
    : (currentOrganization.campaigns || []);

  const { data, setData, put, delete: destroy, processing, reset } = useForm({
    name: template.name,
    body: template.body,
    campaign_id: template.campaignId || '',
    organization_id: currentOrganization.id
  });

  // No router.reload() in onSuccess. TemplatesController redirects back
  // (post/redirect/get), and Inertia already re-renders the dashboard with
  // the fresh props from that redirect. Reloading again fetched the same
  // payload a second time and repainted the page — that double render is
  // visible as a flash right after saving.
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    put(`/templates/${template.id}`, {
      preserveScroll: true,
      onSuccess: () => {
        reset();
        onClose();
      }
    });
  };

  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this template?')) {
      destroy(`/templates/${template.id}`, {
        preserveScroll: true,
        onSuccess: () => {
          onClose();
        }
      });
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal-content glass-panel" style={{ maxWidth: 500 }}>
        <div className="modal-header">
          <h3 style={{ fontSize: '1.25rem', fontWeight: 600 }}>Edit Template</h3>
          <button className="btn-icon" onClick={onClose}><i className="fa-solid fa-xmark"></i></button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Template Name</label>
              <input
                type="text"
                className="form-control"
                value={data.name}
                onChange={e => setData('name', e.target.value)}
                required
              />
            </div>

            {selectableCampaigns.length > 0 && (
              <div className="form-group">
                <label className="form-label">Linked Campaign</label>
                <select
                  className="form-control"
                  value={data.campaign_id}
                  onChange={e => setData('campaign_id', e.target.value)}
                >
                  <option value="">-- No Campaign --</option>
                  {selectableCampaigns.map((c) => (
                    <option key={c.id} value={c.id}>{c.displayName}{c.archivedAt ? ' (archived)' : ''}</option>
                  ))}
                </select>
              </div>
            )}

            {allOrganizations && allOrganizations.length > 0 && (
              <div className="form-group">
                <label className="form-label">Assign To Organization</label>
                <select
                  className="form-control"
                  value={data.organization_id}
                  onChange={e => setData('organization_id', Number(e.target.value))}
                >
                  {allOrganizations.map((org) => (
                    <option key={org.id} value={org.id}>{org.name}</option>
                  ))}
                </select>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginTop: 8 }}>
                  <i className="fa-solid fa-triangle-exclamation" style={{ color: 'var(--warning)', marginRight: 4 }}></i> Reassigning to another organization will immediately remove it from this one.
                </div>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Message Body</label>
              <textarea
                className="form-control"
                rows={4}
                value={data.body}
                onChange={e => setData('body', e.target.value)}
                required
              />
              <TemplateVariablesHint />
            </div>
          </div>
          <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between' }}>
            <button type="button" className="btn btn-danger" onClick={handleDelete} disabled={processing} style={{ background: 'var(--danger, #dc2626)', color: 'white', borderColor: 'var(--danger, #dc2626)', fontWeight: 'bold' }}>
              <i className="fa-solid fa-trash"></i> Delete
            </button>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={processing || !data.name || !data.body}>
                {processing ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

// 4. New Blast Modal
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
const HOURS = Array.from({ length: 24 }, (_, i) => {
  const ampm = i < 12 ? 'AM' : 'PM';
  const h = i % 12 === 0 ? 12 : i % 12;
  return { value: i, label: `${h}:00 ${ampm}` };
});

const NewBlastModal = ({ onClose, onLaunch, organization }: NewBlastModalProps) => {
  const totalSteps = 5;
  const [step, setStep] = useState(1);
  const [showError, setShowError] = useState(false);

  // Step 1 - Target Audience
  const [name, setName] = useState("");
  const [zipCode, setZipCode] = useState(organization.zipCodes?.[0]?.code || "");
  const [testMode, setTestMode] = useState(false);
  const [testPhoneNumbers, setTestPhoneNumbers] = useState("");
  const [reuseExistingLeads, setReuseExistingLeads] = useState(false);
  const [contactLimit, setContactLimit] = useState<string>("");

  // Step 2 - Scheduling & Delivery
  const [startImmediately, setStartImmediately] = useState(true);
  const [startDatetime, setStartDatetime] = useState(() => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
  });
  const [workingHoursStart, setWorkingHoursStart] = useState(9);
  const [workingHoursEnd, setWorkingHoursEnd] = useState(17);
  const [workingDays, setWorkingDays] = useState<string[]>(['Mon', 'Tue', 'Wed', 'Thu', 'Fri']);
  const [messageVolume, setMessageVolume] = useState(10);

  // Archived campaigns are retired from new work everywhere - never offer
  // one here, and never let it be silently pre-selected as a default.
  const activeCampaigns = useMemo(() => organization.campaigns.filter(c => !c.archivedAt), [organization.campaigns]);

  // Step 3 - Provider Settings
  const [campaign, setCampaign] = useState(activeCampaigns[0]?.id);
  const [fromNumber, setFromNumber] = useState(activeCampaigns[0]?.fromNumbers?.[0] || "");

  // Step 4 - Messaging Content
  const [template, setTemplate] = useState(organization.templates[0]?.id);

  // Step 5 - Follow-up
  const [followUpDelayHours, setFollowUpDelayHours] = useState(24);
  const [followUpTemplateId, setFollowUpTemplateId] = useState(organization.templates[0]?.id || null);

  // Default the sending number whenever the picked campaign changes. This
  // used to be a useEffect that also depended on `activeCampaigns`, so any
  // server response that reshaped `organization.campaigns` (a BlastsChannel
  // reload, a template save) silently reset the operator's chosen number
  // mid-wizard and repainted the step. Adjusting during render keys it to
  // the campaign alone and lands in a single paint.
  const [numberForCampaign, setNumberForCampaign] = useState(campaign);
  if (numberForCampaign !== campaign) {
    setNumberForCampaign(campaign);
    setFromNumber(activeCampaigns.find((c) => c.id === campaign)?.fromNumbers?.[0] || "");
  }

  const toggleDay = (day: string) => {
    setWorkingDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);
  };

  const handleNext = () => {
    if (step === 1 && (name.trim().length === 0 || zipCode.length < 5)) {
      setShowError(true);
      return;
    }
    setShowError(false);
    setStep(step + 1);
  };

  const handleLaunch = () => {
    onLaunch({
      name,
      zipCode,
      startDatetime: startImmediately ? null : startDatetime,
      workingHoursStart,
      workingHoursEnd,
      workingDays,
      messageVolume,
      fromNumber,
      campaignId: campaign,
      templateId: template,
      followUpDelayHours,
      followUpTemplateId,
      organizationId: organization.id,
      testPhoneNumbers: testMode ? testPhoneNumbers : "",
      reuseExistingLeads: testMode ? false : reuseExistingLeads,
      contactLimit: testMode ? "" : contactLimit,
    });
  };

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal-content glass-panel">
        <div className="modal-header">
          <h3 style={{ fontSize: '1.25rem', fontWeight: 600 }}>Launch Prospecting Blast</h3>
          <button className="btn-icon" onClick={onClose}><i className="fa-solid fa-xmark"></i></button>
        </div>

        <div className="modal-body">
          {/* Progress Tracker */}
          <div style={{ display: 'flex', marginBottom: 32, gap: 8 }}>
            {Array.from({ length: totalSteps }, (_, i) => (
              <div key={i} style={{ flex: 1, height: 4, borderRadius: 2, background: i < step ? 'var(--accent-1)' : 'rgba(255,255,255,0.1)' }}></div>
            ))}
          </div>

          {step === 1 && (
            <div style={{ animation: 'fadeIn 0.3s ease' }}>
              <h4 style={{ marginBottom: 16, color: 'var(--accent-1)' }}>1. Target Audience</h4>

              <div className="form-group">
                <label className="form-label">Blast Name</label>
                <input
                  className="form-control"
                  type="text"
                  placeholder="e.g. March HVAC Push - 90210"
                  value={name}
                  onChange={e => { setName(e.target.value); setShowError(false); }}
                />
                {showError && name.trim().length === 0 && (
                  <div style={{ color: 'var(--danger, #dc2626)', fontSize: '0.8rem', marginTop: 4 }}>Please enter a name for this blast.</div>
                )}
              </div>

              {/* Test Mode Toggle */}
              <div
                className="glass-panel"
                style={{
                  padding: 16,
                  marginBottom: 20,
                  background: testMode ? 'rgba(236, 72, 153, 0.08)' : 'rgba(0,0,0,0.2)',
                  border: testMode ? '1px solid rgba(236, 72, 153, 0.4)' : '1px solid var(--glass-border)',
                  transition: 'all 0.2s ease',
                }}
              >
                <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', marginBottom: testMode ? 12 : 0 }}>
                  {/* Toggle switch */}
                  <div
                    onClick={() => setTestMode(m => !m)}
                    style={{
                      width: 40, height: 22, borderRadius: 11,
                      background: testMode ? 'var(--accent-2)' : 'rgba(255,255,255,0.15)',
                      position: 'relative', cursor: 'pointer', transition: 'background 0.2s ease', flexShrink: 0,
                    }}
                  >
                    <div style={{
                      position: 'absolute', top: 3, left: testMode ? 21 : 3,
                      width: 16, height: 16, borderRadius: '50%', background: 'white',
                      transition: 'left 0.2s ease', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                    }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.9rem', color: testMode ? 'var(--accent-2)' : 'var(--text-1)' }}>
                      <i className="fa-solid fa-flask" style={{ marginRight: 6 }}></i>
                      Test Mode
                    </div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-3)', marginTop: 2 }}>
                      {testMode ? 'Sending to specific numbers ONLY - all BatchData options below are disabled.' : 'Send to specific phone numbers instead of querying BatchData.'}
                    </div>
                  </div>
                </label>
                {testMode && (
                  <textarea
                    className="form-control"
                    placeholder="e.g. 555-123-4567, 555-987-6543"
                    value={testPhoneNumbers}
                    onChange={e => setTestPhoneNumbers(e.target.value)}
                    rows={2}
                    style={{ marginTop: 4 }}
                  />
                )}
              </div>

              {/* BatchData options - disabled when in test mode */}
              <fieldset disabled={testMode} style={{ border: 'none', padding: 0, margin: 0, opacity: testMode ? 0.4 : 1, transition: 'opacity 0.2s ease', pointerEvents: testMode ? 'none' : 'auto' }}>
                <div className="form-group">
                  <label className="form-label">Target Zip Code</label>
                  {organization.zipCodes && organization.zipCodes.length > 0 ? (
                    <select className="form-control" value={zipCode} onChange={e => { setZipCode(e.target.value); setShowError(false); }}>
                      {organization.zipCodes.map((z) => (
                        <option key={z.id} value={z.code}>
                          {z.code} - {z.city}, {z.state}{z.propertyCount != null ? ` (${z.propertyCount.toLocaleString()} properties)` : ''}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="glass-panel" style={{ padding: 16, color: 'var(--warning)', background: 'rgba(255, 170, 0, 0.1)', border: '1px solid rgba(255, 170, 0, 0.3)' }}>
                      <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 8 }}></i>
                      No Zip Codes claimed yet. Please claim a Zip Code first in your Organization settings.
                    </div>
                  )}
                  {showError && !testMode && zipCode.length < 5 && (
                    <div style={{ color: 'var(--danger, #dc2626)', fontSize: '0.8rem', marginTop: 4 }}>Please select a valid Zip Code.</div>
                  )}
                  <div style={{ marginTop: 8, fontSize: '0.8rem', color: 'var(--text-3)' }}>
                    <i className="fa-solid fa-database" style={{ marginRight: 4 }}></i> Will query BatchData for homeowner contacts in this zip.
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'center' }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 8 }}>
                      <input
                        type="checkbox"
                        checked={reuseExistingLeads}
                        onChange={e => setReuseExistingLeads(e.target.checked)}
                        style={{ accentColor: 'var(--accent-1)', width: 16, height: 16 }}
                      />
                      <span className="form-label" style={{ marginBottom: 0 }}>Reuse existing leads</span>
                    </label>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-3)' }}>
                      Skip BatchData, re-message contacts already in this Zip.
                    </div>
                  </div>

                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Contact Limit <span style={{ fontWeight: 400, color: 'var(--text-3)' }}>(blank = all)</span></label>
                    <input
                      className="form-control"
                      type="number"
                      min={1}
                      placeholder="e.g. 10"
                      value={contactLimit}
                      onChange={e => setContactLimit(e.target.value)}
                    />
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-3)', marginTop: 4 }}>
                      Max contacts to pull from BatchData.
                    </div>
                  </div>
                </div>
              </fieldset>
            </div>
          )}

          {step === 2 && (
            <div style={{ animation: 'fadeIn 0.3s ease' }}>
              <h4 style={{ marginBottom: 16, color: 'var(--accent-1)' }}>2. Scheduling & Delivery</h4>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 8 }}>
                  <input
                    type="checkbox"
                    checked={startImmediately}
                    onChange={e => setStartImmediately(e.target.checked)}
                    style={{ accentColor: 'var(--accent-1)', width: 16, height: 16 }}
                  />
                  <span className="form-label" style={{ marginBottom: 0 }}>Start Immediately</span>
                </label>
                {!startImmediately && (
                  <input
                    className="form-control"
                    type="datetime-local"
                    value={startDatetime}
                    onChange={e => setStartDatetime(e.target.value)}
                  />
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Working Hours (EST)</label>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <select className="form-control" value={workingHoursStart} onChange={e => setWorkingHoursStart(Number(e.target.value))}>
                    {HOURS.map(h => <option key={h.value} value={h.value}>{h.label}</option>)}
                  </select>
                  <span style={{ color: 'var(--text-3)', whiteSpace: 'nowrap' }}>to</span>
                  <select className="form-control" value={workingHoursEnd} onChange={e => setWorkingHoursEnd(Number(e.target.value))}>
                    {HOURS.map(h => <option key={h.value} value={h.value}>{h.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Active Days</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {DAYS.map(day => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => toggleDay(day)}
                      style={{
                        padding: '6px 14px',
                        borderRadius: 6,
                        border: '1px solid',
                        borderColor: workingDays.includes(day) ? 'var(--accent-1)' : 'rgba(255,255,255,0.15)',
                        background: workingDays.includes(day) ? 'rgba(var(--accent-1-rgb, 99,102,241),0.2)' : 'transparent',
                        color: workingDays.includes(day) ? 'var(--accent-1)' : 'var(--text-3)',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: workingDays.includes(day) ? 600 : 400,
                      }}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Message Volume <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>(messages / minute)</span></label>
                <input
                  className="form-control"
                  type="number"
                  min={1}
                  max={100}
                  value={messageVolume}
                  onChange={e => setMessageVolume(Number(e.target.value))}
                />
              </div>
            </div>
          )}

          {step === 3 && (
            <div style={{ animation: 'fadeIn 0.3s ease' }}>
              <h4 style={{ marginBottom: 16, color: 'var(--accent-1)' }}>3. Provider Settings (Signalhouse)</h4>
              <div className="form-group">
                <label className="form-label">10DLC Campaign</label>
                <select className="form-control" value={campaign} onChange={e => setCampaign(Number(e.target.value))}>
                  {activeCampaigns.map(c => <option key={c.id} value={c.id}>{c.displayName}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Sending Number</label>
                <div className="glass-panel" style={{ padding: 16, background: 'rgba(0,0,0,0.2)' }}>
                  {(activeCampaigns.find(c => c.id === campaign) || { fromNumbers: [] }).fromNumbers.map(num => (
                    <label key={num} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="fromNumber"
                        value={num}
                        checked={fromNumber === num}
                        onChange={() => setFromNumber(num)}
                        style={{ accentColor: 'var(--accent-1)' }}
                      />
                      <span>{num}</span>
                      <span className="badge badge-success" style={{ marginLeft: 'auto' }}>Healthy</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div style={{ animation: 'fadeIn 0.3s ease' }}>
              <h4 style={{ marginBottom: 16, color: 'var(--accent-1)' }}>4. Messaging Content</h4>
              <div className="form-group">
                <label className="form-label">Message Template</label>
                <select className="form-control" value={template} onChange={e => setTemplate(Number(e.target.value))}>
                  {organization.templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div className="glass-panel" style={{ padding: 16, background: 'rgba(0,0,0,0.2)' }}>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: 8 }}>Template (variables filled when sent):</div>
                <div style={{ fontStyle: 'italic', fontSize: '0.9rem' }}>
                  "{(organization.templates.find(t => t.id === Number(template)) || { body: '' }).body}"
                </div>
              </div>
            </div>
          )}

          {step === 5 && (
            <div style={{ animation: 'fadeIn 0.3s ease' }}>
              <h4 style={{ marginBottom: 16, color: 'var(--accent-1)' }}>5. Follow-up Message</h4>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-3)', marginBottom: 20 }}>
                A follow-up message sent automatically after the initial contact receives their first message. Highly effective for increasing response rates.
              </p>
              <div className="form-group">
                <label className="form-label">Send Follow-up After <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>(hours)</span></label>
                <input
                  className="form-control"
                  type="number"
                  min={1}
                  value={followUpDelayHours}
                  onChange={e => setFollowUpDelayHours(Number(e.target.value))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Follow-up Template</label>
                <select className="form-control" value={followUpTemplateId ?? ''} onChange={e => setFollowUpTemplateId(Number(e.target.value))}>
                  {organization.templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              {followUpTemplateId && (
                <div className="glass-panel" style={{ padding: 16, background: 'rgba(0,0,0,0.2)' }}>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: 8 }}>Template (variables filled when sent):</div>
                  <div style={{ fontStyle: 'italic', fontSize: '0.9rem' }}>
                    "{(organization.templates.find(t => t.id === Number(followUpTemplateId)) || { body: '' }).body}"
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div>
            {step > 1 && (
              <button className="btn btn-secondary" onClick={() => setStep(step - 1)}>Back</button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={step === totalSteps ? handleLaunch : handleNext}>
              {step === totalSteps ? <span><i className="fa-solid fa-paper-plane"></i> Launch Blast</span> : 'Next Step'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// 4. Blasts View
const campaignStatusBadge = (status?: string | null) => {
  if (!status) return { label: 'Unknown', className: 'badge-neutral' };
  switch (status) {
    case 'ACTIVE':          return { label: 'Active', className: 'badge-success' };
    case 'EXPIRED':
    case 'SUSPENDED':
    case 'REJECTED':
    case 'PENDING_DELETE':  return { label: status.replace(/_/g, ' '), className: 'badge-danger' };
    case 'PENDING_REVIEW':
    case 'PENDING_CREATION': return { label: status.replace(/_/g, ' '), className: 'badge-warning' };
    default:                return { label: status.replace(/_/g, ' '), className: 'badge-neutral' };
  }
};

const blastStatusDescription = (status: string) => {
  switch (status) {
    case 'Queuing...':            return 'Queued and waiting to start.';
    case 'Importing Contacts...': return 'Querying BatchData for homeowner records...';
    case 'Sending...':
    case 'Dispatched to Sender':  return 'Tasks dispatched - messages being delivered.';
    case 'Contacts Acquired':     return 'Contacts acquired, awaiting phone number lookup.';
    case 'Skip Trace Failed':     return 'Skip tracing failed - no phone numbers came back.';
    default:                      return status;
  }
};

const BlastsView = ({ blasts, onNewBlast, organization }: BlastsViewProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  // Hold the selected blast's *id*, not a snapshot of the row object. A
  // snapshot freezes at the moment of the click, so once a BlastsChannel
  // broadcast refreshes `blasts` the detail panel keeps rendering stale
  // counters while the table below it updates - that mismatch is what reads
  // as the panel "flashing" between two versions of the same blast.
  const [selectedBlastId, setSelectedBlastId] = useState<number | null>(null);
  const itemsPerPage = 10;
  const scopedBlasts = (blasts || []).filter((blast: DashboardBlast) => {
    if (!organization?.id || organization.id < 0) return true;
    return blast.organizationId === organization.id;
  });
  const activeBlast = scopedBlasts.find((b: DashboardBlast) => ["Queuing...", "Importing Contacts...", "Sending...", "Dispatched to Sender"].includes(b.status));
  const selectedBlast = selectedBlastId === null ? null : scopedBlasts.find(b => b.id === selectedBlastId) ?? null;
  const displayedBlast = selectedBlast ?? activeBlast;
  const totalPages = Math.max(1, Math.ceil(scopedBlasts.length / itemsPerPage));

  // Reset paging when the workspace changes, using React's documented
  // "adjust state during render" pattern instead of a useEffect. An effect
  // would let one frame paint with the old page number first.
  // https://react.dev/learn/you-might-not-need-an-effect
  const [pagedOrgId, setPagedOrgId] = useState(organization?.id);
  if (pagedOrgId !== organization?.id) {
    setPagedOrgId(organization?.id);
    setCurrentPage(1);
  }

  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * itemsPerPage;
  const visibleBlasts = scopedBlasts.slice(startIndex, startIndex + itemsPerPage);

  // Listen for real-time WebSocket updates via Solid Cable instead of polling
  useEffect(() => {
    const subscription = cableConsumer().subscriptions.create('BlastsChannel', {
      received() {
        // Partial reload: only the `blasts` prop is re-sent and swapped in.
        // router.reload() defaults to preserveState + preserveScroll, so the
        // page component is updated in place rather than remounted.
        router.reload({ only: ['blasts'] });
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return (
    <div className="view-area">
      <div className="page-header">
        <div>
          <h1 className="page-title">Prospecting Blasts</h1>
          <p className="page-subtitle">Manage zip-based outbound SMS launches for {organization.company}</p>
        </div>
        <button className="btn btn-primary" onClick={onNewBlast}>
          <i className="fa-solid fa-plus"></i> New Blast
        </button>
      </div>

      <div className="grid-metrics">
        <div className="metric-card glass-panel" style={{ '--accent-1': 'var(--accent-1)' } as MetricCardStyle}>
          <div className="metric-label">Total Outbound (30d)</div>
          <div className="metric-value">{scopedBlasts.reduce((acc: number, b: DashboardBlast) => acc + (b.sent || 0), 0)}</div>
        </div>
        <div className="metric-card glass-panel" style={{ '--accent-1': 'var(--success)' } as MetricCardStyle}>
          <div className="metric-label">Reply Count</div>
          <div className="metric-value">{scopedBlasts.reduce((acc: number, b: DashboardBlast) => acc + (b.replies || 0), 0)}</div>
        </div>
        <div className="metric-card glass-panel" style={{ '--accent-1': 'var(--warning)' } as MetricCardStyle}>
          <div className="metric-label">Avg Response Rate</div>
          <div className="metric-value">
            {scopedBlasts.length > 0 ? ((scopedBlasts.reduce((acc: number, b: DashboardBlast) => acc + (b.replies || 0), 0) / Math.max(1, scopedBlasts.reduce((acc: number, b: DashboardBlast) => acc + (b.sent || 0), 0))) * 100).toFixed(1) : 0} %
          </div>
        </div>
      </div>

      {displayedBlast && (
        <div className="glass-panel" style={{ padding: 24, marginBottom: 32, borderLeft: `4px solid ${selectedBlast && selectedBlast.id !== activeBlast?.id ? 'var(--text-3)' : 'var(--accent-1)'}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <div>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {(!selectedBlast || selectedBlast.id === activeBlast?.id) && <div className="status-dot"></div>}
                {displayedBlast.name}
              </h3>
              <div style={{ color: 'var(--text-3)', fontSize: '0.875rem', marginTop: 4 }}>
                {blastStatusDescription(displayedBlast.status)}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>
                {displayedBlast.status === 'Importing Contacts...' ? 'Searching...' : `${displayedBlast.sent} / ${displayedBlast.totalContacts}`}
              </div>
              <div style={{ color: 'var(--text-3)', fontSize: '0.875rem' }}>
                {displayedBlast.status === 'Importing Contacts...' ? 'Contacts Found' : 'Contacts Messaged'}
              </div>
            </div>
          </div>
          <div className="progress-track" style={{ background: displayedBlast.status === 'Importing Contacts...' ? 'rgba(99, 102, 241, 0.2)' : 'rgba(255, 255, 255, 0.1)' }}>
            {displayedBlast.status === 'Importing Contacts...' ? (
              <div className="progress-fill" style={{ width: '100%', background: 'linear-gradient(90deg, transparent, rgba(99, 102, 241, 0.8), transparent)', animation: 'slideRight 1.5s infinite linear' }}></div>
            ) : (
              <div className="progress-fill" style={{ width: `${displayedBlast.status === 'Dispatched to Sender' ? 100 : (displayedBlast.sent / Math.max(1, displayedBlast.totalContacts)) * 100}%` }}></div>
            )}
          </div>
        </div>
      )}

      <div className="glass-panel data-table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Name</th>
              <th>Zip Code</th>
              <th>Campaign / Follow-Up</th>
              <th>Status</th>
              <th>Contacts</th>
              <th>Replies</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {visibleBlasts.map((b: DashboardBlast) => (
              <tr
                key={b.id}
                onClick={() => setSelectedBlastId(b.id === selectedBlastId ? null : b.id)}
                style={{ cursor: 'pointer', background: b.id === selectedBlastId ? 'rgba(99, 102, 241, 0.15)' : undefined }}
              >
                <td>{b.date}</td>
                <td style={{ fontWeight: 600 }}>{b.name}</td>
                <td style={{ fontWeight: 600 }}>{b.zipCode}</td>
                <td>
                  <div>{b.campaignName}</div>
                  {b.templateName ? (
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-3)' }}>{b.templateName}</div>
                  ) : (
                    <div style={{ fontSize: '0.75rem', color: 'var(--danger)' }}><i className="fa-solid fa-triangle-exclamation"></i> No message template - won't send</div>
                  )}
                  {b.followUpTemplateName && (
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-3)' }}>Follow-up: {b.followUpTemplateName}</div>
                  )}
                </td>
                <td>
                  <span className={`badge ${b.status === 'Contacts Acquired' ? 'badge-success' : b.status === 'Sending...' ? 'badge-primary' : 'badge-neutral'}`}>
                    {b.status}
                  </span>
                </td>
                <td>{b.sent} {b.totalContacts ? `/ ${b.totalContacts}` : ''}</td>
                <td>{b.replies}</td>
                <td style={{ textAlign: 'right' }}>
                  <Link href={`/blasts/${b.id}`} className="btn btn-secondary" style={{ textDecoration: 'none', padding: '8px 12px' }}>
                    <i className="fa-solid fa-eye"></i> View
                  </Link>
                </td>
              </tr>
            ))}
            {visibleBlasts.length === 0 && (
              <tr>
                <td colSpan={8} style={{ color: 'var(--text-3)', textAlign: 'center', padding: '32px 24px' }}>
                  No blasts found for this organization yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, marginTop: 16 }}>
          <button
            className="btn btn-secondary"
            disabled={safePage === 1}
            onClick={() => setCurrentPage(p => p - 1)}
            style={{ padding: '6px 12px' }}
          >
            <i className="fa-solid fa-chevron-left"></i>
          </button>
          <span style={{ fontSize: '0.9rem', color: 'var(--text-2)' }}>
            Page {safePage} of {totalPages}
          </span>
          <button
            className="btn btn-secondary"
            disabled={safePage === totalPages}
            onClick={() => setCurrentPage(p => p + 1)}
            style={{ padding: '6px 12px' }}
          >
            <i className="fa-solid fa-chevron-right"></i>
          </button>
        </div>
      )}
    </div>
  );
};

const BlastDetailsView = ({ blast }: BlastDetailsViewProps) => {
  if (!blast) return null;

  return (
    <div className="view-area" style={{ animation: 'fadeIn 0.3s ease' }}>
      <div className="page-header" style={{ marginBottom: 24 }}>
        <div>
          <h1 className="page-title">{blast.name}</h1>
          <p className="page-subtitle">Review the current blast status and launch configuration.</p>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <span className={`badge ${blast.status === 'Contacts Acquired' ? 'badge-success' : blast.status === 'Sending...' ? 'badge-primary' : 'badge-neutral'}`} style={{ alignSelf: 'center' }}>
            {blast.status}
          </span>
          <Link href="/blasts" className="btn btn-secondary" style={{ textDecoration: 'none' }}>
            <i className="fa-solid fa-arrow-left"></i> Back to Blasts
          </Link>
        </div>
      </div>

      <div style={{ display: 'grid', gap: 24 }}>
        <div className="glass-panel" style={{ padding: 24 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
            <div className="glass-panel" style={{ padding: 18, background: 'rgba(0,0,0,0.18)' }}>
              <div className="metric-label">Created</div>
              <div style={{ marginTop: 8, fontWeight: 600 }}>{blast.createdAtLabel}</div>
            </div>
            <div className="glass-panel" style={{ padding: 18, background: 'rgba(0,0,0,0.18)' }}>
              <div className="metric-label">Zip Code</div>
              <div style={{ marginTop: 8, fontWeight: 600 }}>{blast.zipCode}</div>
            </div>
            <div className="glass-panel" style={{ padding: 18, background: 'rgba(0,0,0,0.18)' }}>
              <div className="metric-label">Contacts</div>
              <div style={{ marginTop: 8, fontWeight: 600 }}>{blast.sent}</div>
            </div>
            <div className="glass-panel" style={{ padding: 18, background: 'rgba(0,0,0,0.18)' }}>
              <div className="metric-label">Replies</div>
              <div style={{ marginTop: 8, fontWeight: 600 }}>{blast.replies}</div>
            </div>
          </div>
        </div>

        <div className="glass-panel" style={{ padding: 24 }}>
          <h2 style={{ fontSize: '1.1rem', marginBottom: 16 }}>Configuration</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
            <div className="glass-panel" style={{ padding: 18, background: 'rgba(0,0,0,0.18)' }}>
              <div className="metric-label">Campaign</div>
              <div style={{ marginTop: 8, fontWeight: 600 }}>{blast.campaignName}</div>
            </div>
            <div className="glass-panel" style={{ padding: 18, background: 'rgba(0,0,0,0.18)' }}>
              <div className="metric-label">Message Template</div>
              <div style={{ marginTop: 8, fontWeight: 600, color: blast.templateName ? undefined : 'var(--danger)' }}>
                {blast.templateName || 'None - this blast has no message body to send'}
              </div>
            </div>
            <div className="glass-panel" style={{ padding: 18, background: 'rgba(0,0,0,0.18)' }}>
              <div className="metric-label">Follow-Up Template</div>
              <div style={{ marginTop: 8, fontWeight: 600 }}>{blast.followUpTemplateName || 'None'}</div>
            </div>
            <div className="glass-panel" style={{ padding: 18, background: 'rgba(0,0,0,0.18)' }}>
              <div className="metric-label">Start Time</div>
              <div style={{ marginTop: 8, fontWeight: 600 }}>{blast.startDatetimeLabel}</div>
            </div>
            <div className="glass-panel" style={{ padding: 18, background: 'rgba(0,0,0,0.18)' }}>
              <div className="metric-label">Working Hours</div>
              <div style={{ marginTop: 8, fontWeight: 600 }}>{blast.workingHoursLabel}</div>
            </div>
            <div className="glass-panel" style={{ padding: 18, background: 'rgba(0,0,0,0.18)' }}>
              <div className="metric-label">Working Days</div>
              <div style={{ marginTop: 8, fontWeight: 600 }}>{blast.workingDaysLabel}</div>
            </div>
            <div className="glass-panel" style={{ padding: 18, background: 'rgba(0,0,0,0.18)' }}>
              <div className="metric-label">Message Volume</div>
              <div style={{ marginTop: 8, fontWeight: 600 }}>{blast.messageVolume ?? 'Not set'}</div>
            </div>
            <div className="glass-panel" style={{ padding: 18, background: 'rgba(0,0,0,0.18)' }}>
              <div className="metric-label">Follow-Up Delay</div>
              <div style={{ marginTop: 8, fontWeight: 600 }}>{blast.followUpDelayLabel}</div>
            </div>
            <div className="glass-panel" style={{ padding: 18, background: 'rgba(0,0,0,0.18)' }}>
              <div className="metric-label">Sending Number</div>
              <div style={{ marginTop: 8, fontWeight: 600 }}>
                {blast.fromNumber || 'None assigned'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// 5. Inbox View
const InboxView = ({ leads, setLeads, organization }: InboxViewProps) => {
  const scopedLeads = (leads || []).filter(l => !organization?.id || organization.id < 0 || l.organizationId === organization.id);
  const replyLeads = scopedLeads.filter((l) => l.messages && l.messages.length > 0);

  // Only the user's explicit pick lives in state. Which thread is actually
  // shown is derived during render, so a `leads` refresh that drops or
  // reorders threads resolves in the same paint. Doing this in a useEffect
  // instead (setActiveLeadId in an effect keyed on `replyLeads`) fired on
  // every single render - `replyLeads` is rebuilt each time - and painted an
  // empty conversation pane before correcting itself.
  const [pickedLeadId, setPickedLeadId] = useState<number | null>(null);
  const activeLead = (pickedLeadId !== null && replyLeads.find(l => l.id === pickedLeadId)) || replyLeads[0] || null;
  const activeLeadId = activeLead?.id ?? null;

  const [chatInput, setChatInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Scroll to the newest message when the thread changes or grows. Keyed on
  // the message count rather than the whole `leads` array - `leads` gets a
  // new identity on every server response, which made the pane yank itself
  // back to the bottom during unrelated navigation.
  const activeMessageCount = activeLead?.messages?.length ?? 0;
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "auto", block: "end" });
  }, [activeLeadId, activeMessageCount]);

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatInput.trim() || !activeLeadId) return;

    const tempMessage: DashboardMessage = {
      id: Date.now(),
      dir: "out",
      text: chatInput,
      createdAt: new Date().toISOString()
    };

    // Server-side message count before the optimistic append, so the response
    // can be checked for whether the send actually landed.
    const countBefore = activeLead?.messages?.length ?? 0;

    setLeads(current => current.map(l => l.id === activeLeadId ? { ...l, messages: [...(l.messages || []), tempMessage] } : l));
    const content = chatInput;
    setChatInput('');

    // A rejected send comes back as a redirect carrying a flash alert, which
    // Inertia treats as a successful visit - so onError never fires and the
    // only reliable signal is whether the thread actually grew. The optimistic
    // bubble self-corrects (the redirect ships fresh `leads` props), but the
    // typed text would otherwise be gone with no way to recover it. Put it
    // back so a failed send costs a click, not the message.
    const restoreOnFailure = (page: { props: Record<string, unknown> }) => {
      const leadsProp = page.props.leads as DashboardLead[] | undefined;
      const countAfter = leadsProp?.find(l => l.id === activeLeadId)?.messages?.length ?? 0;
      if (countAfter <= countBefore) setChatInput(current => current || content);
    };

    router.post(`/leads/${activeLeadId}/messages`, { content }, {
      preserveScroll: true,
      preserveState: true,
      onSuccess: restoreOnFailure,
      onError: () => setChatInput(current => current || content),
    });
  };

  // Mark the open thread read - at most one request per (thread, message
  // count). Without the guard this effect re-entered itself: the PUT
  // redirects back with a fresh `leads` prop, the effect re-runs on the new
  // array, and if that response was serialised a beat before the write
  // landed it fired the PUT again, re-rendering the whole dashboard in a
  // loop. Keying on the message count rather than the lead id alone means a
  // genuinely new inbound message re-arms it.
  const markedReadRef = useRef<string | null>(null);
  const activeLeadUnread = activeLead?.hasUnreadMessages ?? false;
  useEffect(() => {
    if (activeLeadId === null || !activeLeadUnread) return;

    const token = `${activeLeadId}:${activeMessageCount}`;
    if (markedReadRef.current === token) return;
    markedReadRef.current = token;

    setLeads(current => current.map(l => l.id === activeLeadId ? { ...l, hasUnreadMessages: false } : l));
    router.put(`/leads/${activeLeadId}/mark_read`, {}, { preserveScroll: true, preserveState: true });
  }, [activeLeadId, activeLeadUnread, activeMessageCount, setLeads]);

  const [contactLead, setContactLead] = useState<DashboardLead | null>(null);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 24, minHeight: 0 }}>
      <div className="page-header" style={{ marginBottom: 24 }}>
        <div>
          <h1 className="page-title">Lead Inbox</h1>
          <p className="page-subtitle">Review responses and book calls on {organization.company}'s calendar</p>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <select className="form-control" style={{ width: 'auto' }}>
            <option>Filter: Positive Intents</option>
            <option>Filter: All Intents</option>
          </select>
        </div>
      </div>

      <div className="inbox-layout" style={{ flex: 1, minHeight: 0 }}>
        <div className="inbox-list glass-panel">
          {replyLeads.length > 0 ? (
            replyLeads.map((lead) => (
              <div
                key={lead.id}
                className={`lead-card ${lead.id === activeLeadId ? 'active' : ''}`}
                onClick={() => setPickedLeadId(lead.id)}
                style={{ position: 'relative' }}
              >
                {lead.hasUnreadMessages && (
                  <div style={{ position: 'absolute', top: 12, right: 12, width: 8, height: 8, borderRadius: '50%', background: 'var(--danger)' }} />
                )}
                <div className="lead-header">
                  <div style={{ fontWeight: 600 }}>{lead.name} <span style={{ color: 'var(--text-3)', fontSize: '0.8rem', fontWeight: 400 }}>{lead.phone}</span></div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-3)' }}>{lead.lastActivity}</div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span className={`badge ${lead.intent === 'positive' ? 'badge-success' : 'badge-neutral'}`}>
                    {lead.intent} intent
                  </span>
                  <span style={{ fontSize: '0.75rem', color: lead.status === 'scheduled' ? 'var(--success)' : 'var(--text-3)' }}>
                    {lead.status.toUpperCase()}
                  </span>
                </div>
                <div className="lead-preview">
                  {lead.messages && lead.messages.length > 0 ? lead.messages[lead.messages.length - 1].text : ''}
                </div>
              </div>
            ))
          ) : (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-3)' }}>
              No inbound replies yet.
            </div>
          )}
        </div>

        <div className="conversation-view glass-panel">
          {activeLead ? (
            <>
              <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--glass-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(0,0,0,0.2)' }}>
                <div>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 600 }}>{activeLead.name}</h3>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-3)' }}>{activeLead.phone}</div>
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <button className="btn btn-secondary" onClick={() => setContactLead(activeLead)}>
                    <i className="fa-solid fa-address-card"></i> View Contact
                  </button>
                  {/*
                    Booking isn't built. This used to flip the button green and
                    say "Call Scheduled" off nothing but local state - telling
                    the operator a call was on the calendar when none existed.
                    The title lives on the wrapper because a disabled button
                    fires no pointer events, so a title on the button itself
                    never shows.
                  */}
                  <span title={activeLead.status === 'scheduled' ? undefined : 'Not implemented'} style={{ display: 'inline-flex' }}>
                    <button
                      className="btn btn-primary"
                      disabled
                      style={{
                        background: activeLead.status === 'scheduled' ? 'var(--success)' : '',
                        cursor: activeLead.status === 'scheduled' ? 'default' : 'not-allowed'
                      }}
                    >
                      <i className="fa-solid fa-calendar-check"></i>
                      {activeLead.status === 'scheduled' ? 'Call Scheduled' : 'Book on Calendar'}
                    </button>
                  </span>
                </div>
              </div>

              <div className="chat-history">
                {activeLead.messages && activeLead.messages.map((msg, index) => {
                  return (
                    <div key={`${msg.id}-${index}`} className={`message-bubble ${msg.dir === 'out' ? 'message-out' : 'message-in'}`}>
                      <div style={{ fontSize: '0.75rem', opacity: 0.7, marginBottom: 4 }}>
                        {msg.dir === 'out' ? organization.company : activeLead.name}
                      </div>
                      {msg.text}
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
              <div className="chat-input-area" style={{ padding: '16px 24px', borderTop: '1px solid var(--glass-border)' }}>
                <form onSubmit={handleSendMessage} style={{ display: 'flex', gap: 12 }}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Type a message..."
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <button type="submit" className="btn btn-primary" disabled={!chatInput.trim()}>
                    <i className="fa-solid fa-paper-plane"></i> Send
                  </button>
                </form>
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-3)' }}>
              No replied leads to review yet
            </div>
          )}
        </div>
      </div>

      <ViewContactModal lead={contactLead} onClose={() => setContactLead(null)} />
    </div>
  );
};

// 5.5 View Contact Modal
//
// Everything here already ships with each lead from
// ApplicationController#render_dashboard - working a reply means knowing whose
// property you're pitching, and the address appears nowhere else in the
// conversation pane.
const ViewContactModal = ({ lead, onClose }: ViewContactModalProps) => {
  useEffect(() => {
    if (!lead) return;

    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [lead, onClose]);

  if (!lead) return null;

  const field = (label: string, value: React.ReactNode, span = false) => (
    <div style={span ? { gridColumn: '1 / -1' } : undefined}>
      <span style={{ color: 'var(--text-3)', fontSize: '0.85rem', display: 'block', marginBottom: 4 }}>{label}</span>
      <div style={{ fontWeight: 500 }}>{value}</div>
    </div>
  );

  return (
    <div className="modal-overlay" style={{ zIndex: 100 }} onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal-content glass-panel" style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h3 style={{ fontSize: '1.25rem', fontWeight: 600 }}>
            <i className="fa-solid fa-address-card" style={{ marginRight: 8 }}></i> {lead.name}
          </h3>
          <button type="button" className="btn-icon" onClick={onClose}><i className="fa-solid fa-xmark"></i></button>
        </div>
        <div className="modal-body" style={{ padding: 24 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            {field('Phone', lead.phone || 'Unknown')}
            {field('Zip', lead.zip || 'Unknown')}
            {field('Property Address', lead.address || 'Unknown', true)}

            <div style={{ gridColumn: '1 / -1', borderTop: '1px solid rgba(255,255,255,0.1)' }}></div>

            {field('Status', (
              <span style={{ color: lead.status === 'scheduled' ? 'var(--success)' : undefined }}>
                {lead.status.toUpperCase()}
              </span>
            ))}
            {field('Intent', (
              <span className={`badge ${lead.intent === 'positive' ? 'badge-success' : 'badge-neutral'}`}>
                {lead.intent}
              </span>
            ))}
            {field('Source', lead.source || 'Unknown')}
            {field('Last Activity', lead.updatedAtLabel || lead.lastActivity)}
          </div>
        </div>
      </div>
    </div>
  );
};

// 6. Leads View
const LeadsView = ({ leads, leadStatuses, organization }: LeadsViewProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [sortDirection, setSortDirection] = useState("desc");
  const [deletingLeadId, setDeletingLeadId] = useState<number | null>(null);
  const itemsPerPage = 20;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedStatus, sortDirection, leads.length]);

  const scopedLeads = (leads || []).filter(l => !organization?.id || organization.id < 0 || l.organizationId === organization.id);
  const searchTokens = searchQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const filteredLeads = scopedLeads.filter((lead: DashboardLead) => {
    const searchableValues = [
      lead.id,
      lead.name,
      lead.address,
      lead.phone,
      lead.zip,
      lead.status,
      lead.intent,
      lead.source
    ].filter((value) => value !== null && value !== undefined).map(String);
    const normalizedValues = searchableValues.map((value) => value.toLowerCase());
    const digitValues = searchableValues.map((value) => value.replace(/\D/g, "")).filter(Boolean);
    const matchesSearch = searchTokens.every((token) => {
      if (normalizedValues.some((value) => value.includes(token))) return true;

      const tokenDigits = token.replace(/\D/g, "");
      return tokenDigits.length > 0 && digitValues.some((value) => value.includes(tokenDigits));
    });
    const matchesStatus = selectedStatus === "all" || lead.status === selectedStatus;

    return matchesSearch && matchesStatus;
  });

  const sortedLeads = [ ...filteredLeads ].sort((a, b) => {
    const aTime = new Date(a.updatedAt).getTime();
    const bTime = new Date(b.updatedAt).getTime();

    return sortDirection === "asc" ? aTime - bTime : bTime - aTime;
  });

  const totalPages = Math.max(1, Math.ceil(sortedLeads.length / itemsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * itemsPerPage;
  const visibleLeads = sortedLeads.slice(startIndex, startIndex + itemsPerPage);

  const handleDelete = (lead: DashboardLead) => {
    if (!window.confirm(`Delete lead "${lead.name}"? This action cannot be undone.`)) {
      return;
    }

    setDeletingLeadId(lead.id);
    router.delete(`/leads/${lead.id}`, {
      preserveScroll: true,
      onFinish: () => setDeletingLeadId(null)
    });
  };

  return (
    <div className="view-area">
      <div className="page-header">
        <div>
          <h1 className="page-title">Leads Directory ({scopedLeads.length})</h1>
          <p className="page-subtitle">Review normalized BatchData queries and engagement statuses.</p>
          <p className="page-subtitle" style={{ marginTop: 4 }}>
            Showing {filteredLeads.length} of {scopedLeads.length} leads
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <input
            type="search"
            className="form-control"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search name, address, or phone"
            style={{ minWidth: 280 }}
          />
          <select
            className="form-control"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            style={{ width: 'auto', minWidth: 150 }}
          >
            <option value="all">All statuses</option>
            {leadStatuses.map((status: string) => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
          <select
            className="form-control"
            value={sortDirection}
            onChange={(e) => setSortDirection(e.target.value)}
            style={{ width: 'auto', minWidth: 170 }}
          >
            <option value="desc">Updated: newest first</option>
            <option value="asc">Updated: oldest first</option>
          </select>
          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              className="btn btn-secondary"
              disabled={safePage === 1}
              onClick={() => setCurrentPage(p => p - 1)}
              style={{ padding: '6px 12px' }}
            >
              <i className="fa-solid fa-chevron-left"></i>
            </button>
            <span style={{ fontSize: '0.9rem', color: 'var(--text-2)' }}>
              Page {safePage} of {totalPages}
            </span>
            <button
              className="btn btn-secondary"
              disabled={safePage === totalPages}
              onClick={() => setCurrentPage(p => p + 1)}
              style={{ padding: '6px 12px' }}
            >
              <i className="fa-solid fa-chevron-right"></i>
            </button>
            </div>
          )}
        </div>
      </div>
      <div className="glass-panel data-table-wrapper" style={{ flex: 1, minHeight: 0 }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Address</th>
              <th>Phone Number</th>
              <th>Status</th>
              <th>Updated</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {visibleLeads.map(l => (
              <tr key={l.id}>
                <td style={{ fontWeight: 500 }}>{l.name}</td>
                <td style={{ color: 'var(--text-2)' }}>{l.address}</td>
                <td style={{ color: 'var(--text-2)' }}>{l.phone}</td>
                <td>
                  <span className={`badge ${l.status === 'scheduled' ? 'badge-success' : l.status === 'messaged' ? 'badge-primary' : 'badge-neutral'}`}>
                    {l.status}
                  </span>
                </td>
                <td style={{ color: 'var(--text-2)' }}>{l.updatedAtLabel}</td>
                <td style={{ textAlign: 'right' }}>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => handleDelete(l)}
                    disabled={deletingLeadId === l.id}
                    title="Delete lead"
                    style={{ padding: '8px 12px', minWidth: 112 }}
                  >
                    <i className="fa-solid fa-trash"></i> {deletingLeadId === l.id ? 'Deleting...' : 'Delete'}
                  </button>
                </td>
              </tr>
            ))}
            {visibleLeads.length === 0 && (
              <tr>
                <td colSpan={6} style={{ color: 'var(--text-3)', textAlign: 'center', padding: '32px 24px' }}>
                  No leads match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// 7. View Campaign Modal
const ViewCampaignModal = ({ campaign, onClose }: ViewCampaignModalProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [nickname, setNickname] = useState(campaign?.nickname || "");

  // Reset the draft only when a *different* campaign is opened. The old
  // effect depended on the `campaign` object, which gets a fresh identity on
  // every dashboard response, so an unrelated background reload would drop
  // the operator out of edit mode and snap the field back to the saved value.
  const [draftForCampaignId, setDraftForCampaignId] = useState(campaign?.id ?? null);
  if (draftForCampaignId !== (campaign?.id ?? null)) {
    setDraftForCampaignId(campaign?.id ?? null);
    setNickname(campaign?.nickname || "");
    setIsEditing(false);
  }

  if (!campaign) return null;

  return (
    <div className="modal-overlay" style={{ zIndex: 100 }} onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal-content glass-panel" style={{ maxWidth: 700 }}>
        <div className="modal-header" style={{ alignItems: 'flex-start' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 600, flex: 1, minWidth: 0, marginRight: 16 }}>
            {isEditing ? (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <input
                  type="text"
                  style={{
                    padding: '6px 12px',
                    fontSize: '1rem',
                    height: '36px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid var(--accent-1)',
                    color: '#fff',
                    borderRadius: '4px',
                    outline: 'none',
                    flex: '1 1 200px'
                  }}
                  value={nickname}
                  onChange={e => setNickname(e.target.value)}
                  autoFocus
                />
                <button className="btn btn-primary" style={{ padding: '4px 16px', height: '36px' }} onClick={() => {
                  router.patch(`/campaigns/${campaign.id}`, { campaign: { nickname } }, {
                    preserveScroll: true,
                    onSuccess: () => setIsEditing(false)
                  });
                }}>Save</button>
                <button className="btn btn-secondary" style={{ padding: '4px 16px', height: '36px' }} onClick={() => {
                  setNickname(campaign.nickname || "");
                  setIsEditing(false);
                }}>Cancel</button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                {campaign.displayName} Details
                <button className="btn btn-secondary btn-sm" onClick={() => setIsEditing(true)} style={{ fontSize: '0.85rem' }}>
                  <i className="fa-solid fa-pen-to-square"></i> Edit Nickname
                </button>
              </div>
            )}
          </h3>
          <button type="button" className="btn-icon" style={{ marginTop: 4 }} onClick={onClose}><i className="fa-solid fa-xmark"></i></button>
        </div>
        <div className="modal-body" style={{ padding: 24, maxHeight: '70vh', overflowY: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
            <div>
              <span style={{ color: 'var(--text-3)', fontSize: '0.85rem' }}>Source</span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
                <span className={`badge ${campaign.source === 'signal_house' ? 'badge-primary' : 'badge-warning'}`}>
                  {campaign.source === 'signal_house' ? 'SignalHouse' : campaign.source === 'signal_mash' ? 'SignalMash' : campaign.source}
                </span>
                <span style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{campaign.source_id}</span>
              </div>
            </div>

            {campaign.external_metadata && (
              <>
                <div>
                  <span style={{ color: 'var(--text-3)', fontSize: '0.85rem', display: 'block', marginBottom: 4 }}>Status</span>
                  <span className={`badge ${campaign.external_metadata.status === 'ACTIVE' ? 'badge-success' : 'badge-warning'}`}>
                    {campaign.external_metadata.status || 'UNKNOWN'}
                  </span>
                </div>

                <div style={{ gridColumn: '1 / -1', borderTop: '1px solid rgba(255,255,255,0.1)', margin: '4px 0' }}></div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <span style={{ color: 'var(--text-3)', fontSize: '0.85rem', display: 'block', marginBottom: 4 }}>Description</span>
                  <div style={{ fontWeight: 500, fontSize: '0.9rem', lineHeight: 1.4, color: 'var(--text-2)' }}>{campaign.external_metadata.description || 'N/A'}</div>
                </div>

                <div style={{ gridColumn: '1 / -1', borderTop: '1px solid rgba(255,255,255,0.1)', margin: '4px 0' }}></div>

                <div>
                  <span style={{ color: 'var(--text-3)', fontSize: '0.85rem', display: 'block', marginBottom: 4 }}>Brand ID</span>
                  <div style={{ fontWeight: 500 }}>{campaign.external_metadata.brandId || 'N/A'}</div>
                </div>
                <div>
                  <span style={{ color: 'var(--text-3)', fontSize: '0.85rem', display: 'block', marginBottom: 4 }}>CSP ID</span>
                  <div style={{ fontWeight: 500 }}>{campaign.external_metadata.cspId || 'N/A'}</div>
                </div>

                <div>
                  <span style={{ color: 'var(--text-3)', fontSize: '0.85rem', display: 'block', marginBottom: 4 }}>Usecase</span>
                  <div style={{ fontWeight: 500 }}>{campaign.external_metadata.usecase || 'N/A'}</div>
                </div>
                <div>
                  <span style={{ color: 'var(--text-3)', fontSize: '0.85rem', display: 'block', marginBottom: 4 }}>Created</span>
                  <div style={{ fontWeight: 500 }}>
                    {campaign.external_metadata.createDate ? new Date(campaign.external_metadata.createDate).toLocaleDateString() : 'N/A'}
                  </div>
                </div>

                <div style={{ gridColumn: '1 / -1', borderTop: '1px solid rgba(255,255,255,0.1)', margin: '4px 0' }}></div>

                <div>
                  <span style={{ color: 'var(--text-3)', fontSize: '0.85rem', display: 'block', marginBottom: 4 }}>Opt-in Keywords</span>
                  <div style={{ fontWeight: 500 }}>{campaign.external_metadata.optinKeywords || 'N/A'}</div>
                </div>
                <div>
                  <span style={{ color: 'var(--text-3)', fontSize: '0.85rem', display: 'block', marginBottom: 4 }}>Opt-out Keywords</span>
                  <div style={{ fontWeight: 500 }}>{campaign.external_metadata.optoutKeywords || 'N/A'}</div>
                </div>
                <div>
                  <span style={{ color: 'var(--text-3)', fontSize: '0.85rem', display: 'block', marginBottom: 4 }}>Help Keywords</span>
                  <div style={{ fontWeight: 500 }}>{campaign.external_metadata.helpKeywords || 'N/A'}</div>
                </div>
              </>
            )}
          </div>

          <div>
            <span style={{ color: 'var(--text-3)', fontSize: '0.85rem', display: 'block', marginBottom: 8 }}>
              Sending Numbers ({campaign.fromNumbers.length})
            </span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {campaign.fromNumbers.length > 0 ? (
                campaign.fromNumbers.map(num => (
                  <div key={num} style={{ background: 'rgba(255,255,255,0.05)', padding: '6px 12px', borderRadius: 4, border: '1px solid var(--border-color)' }}>
                    {num}
                  </div>
                ))
              ) : (
                <div style={{ color: 'var(--text-3)', fontSize: '0.9rem', fontStyle: 'italic' }}>No numbers synced for this campaign.</div>
              )}
            </div>
          </div>
        </div>
        <div className="modal-footer" style={{ borderTop: '1px solid var(--border)', padding: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// 7. New Campaign Modal
const NewCampaignModal = ({ onClose, currentOrganization }: NewCampaignModalProps) => {
  const { data, setData, post, processing, errors, reset } = useForm({
    campaign_id: '',
    source: 'signal_house',
    organization_id: currentOrganization.id
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    post('/campaigns', {
      // Inertia fires onSuccess for any 2xx page visit - including the redirect
      // that Rails serves when the controller rescued a failure (CSRF rejection,
      // SignalMash/SignalHouse API error). Those set flash.error/flash.alert, NOT
      // flash.success. Only tear down the modal on a genuine sync so a failure can
      // never masquerade as success - otherwise the user sees the modal close with
      // no synced campaign and no error.
      onSuccess: (page) => {
        const flash = (page?.props as any)?.flash;
        if (flash?.error || flash?.alert || !flash?.success) return;
        reset();
        onClose();
      }
    });
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 100 }} onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal-content glass-panel" style={{ maxWidth: 500 }}>
        <div className="modal-header">
          <h3 style={{ fontSize: '1.25rem', fontWeight: 600 }}>Sync External Campaign</h3>
          <button type="button" className="btn-icon" onClick={onClose}><i className="fa-solid fa-xmark"></i></button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <p style={{ color: 'var(--text-3)', fontSize: '0.9rem', marginBottom: 16 }}>
              Select the source platform and paste your Campaign ID to sync its details and associated phone numbers into FillMyCalendar.
            </p>

            <div className="form-group">
              <label className="form-label">Campaign Source</label>
              <select
                className="form-control"
                value={data.source}
                onChange={e => setData('source', e.target.value)}
                required
              >
                <option value="signal_house">SignalHouse</option>
                <option value="signal_mash">SignalMash</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Campaign ID</label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. C0LTDZF"
                value={data.campaign_id}
                onChange={e => setData('campaign_id', e.target.value)}
                required
              />
            </div>
            {errors.campaign_id && (
              <div style={{ color: 'var(--danger)', fontSize: '0.85rem', marginTop: -8, marginBottom: 16 }}>
                {errors.campaign_id}
              </div>
            )}
          </div>

          <div className="modal-footer" style={{ borderTop: '1px solid rgba(255,255,255,0.1)', padding: '16px 24px', display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={processing}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={processing}>
              {processing ? 'Syncing...' : 'Sync Campaign'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// 9. New Organization Modal
const NewOrganizationModal = ({ onClose }: NewOrganizationModalProps) => {
  const { data, setData, post, processing, errors, reset } = useForm({
    name: '',
    status: 'active',
    industry: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    post('/organizations', {
      onSuccess: () => { reset(); onClose(); }
    });
  };

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal-content glass-panel">
        <div className="modal-header">
          <h3 style={{ fontSize: '1.25rem', fontWeight: 600 }}>New Organization</h3>
          <button className="btn-icon" onClick={onClose}><i className="fa-solid fa-xmark"></i></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Organization Name</label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. Acme Corp"
                value={data.name}
                onChange={e => setData('name', e.target.value)}
                autoFocus
              />
              {errors.name && <div style={{ marginTop: 6, fontSize: '0.8rem', color: 'var(--danger)' }}>{errors.name}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Industry</label>
              <select
                className="form-control"
                value={data.industry}
                onChange={e => setData('industry', e.target.value)}
              >
                <option value="">Select an industry</option>
                <option value="arborist">Arborist</option>
                <option value="roofing">Roofing</option>
                <option value="residential_contractor">Residential Contractor</option>
                <option value="landscaper">Landscaper</option>
              </select>
              {errors.industry && <div style={{ marginTop: 6, fontSize: '0.8rem', color: 'var(--danger)' }}>{errors.industry}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                className="form-control"
                value={data.status}
                onChange={e => setData('status', e.target.value)}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={processing || !data.name.trim()}>
              {processing ? 'Creating...' : <><i className="fa-solid fa-plus"></i> Create Organization</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// 9.5 Edit Organization Modal
const EditOrganizationModal = ({ organization, onClose }: EditOrganizationModalProps) => {
  const { data, setData, patch, processing, errors, reset } = useForm({
    name: organization.name || '',
    status: organization.status || 'active',
    industry: organization.industry || ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    patch(`/organizations/${organization.id}`, {
      onSuccess: () => { reset(); onClose(); }
    });
  };

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal-content glass-panel" style={{ maxWidth: 500 }}>
        <div className="modal-header">
          <h3 style={{ fontSize: '1.25rem', fontWeight: 600 }}>Edit Organization</h3>
          <button className="btn-icon" onClick={onClose}><i className="fa-solid fa-xmark"></i></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Organization Name</label>
              <input
                type="text"
                className="form-control"
                value={data.name}
                onChange={e => setData('name', e.target.value)}
                required
              />
              {errors.name && <div style={{ marginTop: 6, fontSize: '0.8rem', color: 'var(--danger)' }}>{errors.name}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">Industry</label>
              <select
                className="form-control"
                value={data.industry || ""}
                onChange={e => setData('industry', e.target.value)}
              >
                <option value="">Select an Industry</option>
                <option value="arborist">Arborist</option>
                <option value="roofing">Roofing</option>
                <option value="residential_contractor">Residential Contractor</option>
                <option value="landscaper">Landscaper</option>
              </select>
              {errors.industry && <div style={{ marginTop: 6, fontSize: '0.8rem', color: 'var(--danger)' }}>{errors.industry}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                className="form-control"
                value={data.status}
                onChange={e => setData('status', e.target.value)}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={processing || !data.name.trim() || !data.industry}>
              {processing ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// 10. Organizations View
const OrganizationsView = ({ organizations, activeOrganizationId, onNew, onSwitch, onEdit }: OrganizationsViewProps) => (
  <div className="view-area">
    <div className="page-header">
      <div>
        <h1 className="page-title">Organizations</h1>
        <p className="page-subtitle">Manage organizations and their associated campaigns</p>
      </div>
      <button className="btn btn-primary" onClick={onNew}>
        <i className="fa-solid fa-plus"></i> New Organization
      </button>
    </div>
    <div className="glass-panel data-table-wrapper">
      <table className="data-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Status</th>
            <th>Campaigns</th>
            <th>Leads</th>
            <th>Created</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {organizations && organizations.length > 0 ? (
            organizations.map((org) => (
              <tr key={org.id}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 6, background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <i className="fa-solid fa-sitemap" style={{ color: 'var(--accent-1)' }}></i>
                    </div>
                    <span style={{ fontWeight: 600 }}>{org.name}</span>
                  </div>
                </td>
                <td>
                  <span className={`badge ${org.status === 'active' ? 'badge-success' : 'badge-neutral'}`}>
                    {org.status}
                  </span>
                </td>
                <td style={{ color: 'var(--text-2)' }}>{org.campaignsCount}</td>
                <td style={{ color: 'var(--text-2)' }}>{org.leadsCount}</td>
                <td style={{ color: 'var(--text-3)' }}>{org.createdAt}</td>
                <td style={{ display: 'flex', gap: '8px' }}>
                  {org.id !== activeOrganizationId && (
                    <button onClick={() => onSwitch(org.id)} className="btn btn-primary" style={{ padding: '4px 12px', fontSize: '0.8rem' }}>
                      Switch To
                    </button>
                  )}
                  <button onClick={() => onEdit(org)} className="btn btn-secondary" style={{ padding: '4px 12px', fontSize: '0.8rem' }}>
                    Edit
                  </button>
                  <Link href={`/organizations/${org.id}`} className="btn btn-secondary" style={{ padding: '4px 12px', fontSize: '0.8rem', display: 'inline-block', textDecoration: 'none' }}>
                    View
                  </Link>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-3)', padding: 48 }}>
                No organizations yet. Click <strong style={{ color: 'var(--text-2)' }}>New Organization</strong> to create one.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);
// --- NEW COMPONENT: AddZipcodeModal ---
const AddZipcodeModal = ({ organization, existingZipCodeIds, onClose }: AddZipcodeModalProps) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<DashboardZipCode[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedZip, setSelectedZip] = useState<DashboardZipCode | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const {
    data: zipForm,
    setData: setZipForm,
    post: submitZip,
    processing: isAddingZip,
    errors: zipErrors,
    clearErrors: clearZipErrors,
    reset: resetZipForm
  } = useForm<{ zip_code_id: number | null }>({ zip_code_id: null });

  useEffect(() => {
    if (query.trim().length === 0) {
      setResults([]);
      setIsSearching(false);
      return;
    }

    const request = new AbortController();
    const delayDebounceFn = setTimeout(() => {
      setIsSearching(true);
      fetch(`/zip_codes?q=${encodeURIComponent(query)}&industry=${encodeURIComponent(organization.industry || '')}`, {
        headers: { 'Accept': 'application/json' },
        signal: request.signal
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Zip code search failed (${response.status})`);
          return response.json();
        })
        .then((data: DashboardZipCode[]) => {
          setResults(data);
          setIsSearching(false);
        })
        .catch((error: Error) => {
          if (error.name === 'AbortError') return;
          console.error(error);
          setIsSearching(false);
        });
    }, 300);

    return () => {
      clearTimeout(delayDebounceFn);
      request.abort();
    };
  }, [query, organization.industry]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && event.target instanceof Node && !dropdownRef.current.contains(event.target)) {
        setResults([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const claimSelectedZipCode = () => {
    if (!selectedZip) return;

    if (existingZipCodeIds.includes(selectedZip.id)) {
      alert("You already claimed this Zip Code.");
      return;
    }

    if (zipForm.zip_code_id !== selectedZip.id) return;

    submitZip(`/organizations/${organization.id}/zip_codes`, {
      preserveScroll: true,
      onSuccess: () => {
        resetZipForm();
        onClose();
      }
    });
  };

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal-content glass-panel" style={{ maxWidth: 500, overflow: 'visible' }}>
        <div className="modal-header">
          <h3 style={{ fontSize: '1.25rem', fontWeight: 600 }}>Reserve Zip Code</h3>
          <button className="btn-icon" onClick={onClose}><i className="fa-solid fa-xmark"></i></button>
        </div>
        <div className="modal-body" style={{ minHeight: 250 }}>
          <p style={{ color: 'var(--text-2)', fontSize: '0.9rem', marginBottom: 16 }}>
            Search for an available Zip Code. These results are pre-filtered to exclude any Zip Codes actively claimed by another <strong>{organization.industry}</strong>.
          </p>

          <div ref={dropdownRef} style={{ position: 'relative', width: '100%' }}>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                className="form-control"
                placeholder="Type a prefix (e.g. 190) or City..."
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  if (selectedZip) {
                    setSelectedZip(null);
                    setZipForm('zip_code_id', null);
                  }
                  clearZipErrors();
                }}
                style={{ paddingLeft: 36 }}
                autoFocus
              />
              <i className={`fa-solid ${isSearching ? 'fa-spinner fa-spin' : 'fa-magnifying-glass'}`} style={{ position: 'absolute', left: 12, top: 12, color: 'var(--text-3)' }}></i>
            </div>

            {results.length > 0 && !selectedZip && (
              <div className="glass-panel" style={{ position: 'absolute', top: '100%', right: 0, left: 0, zIndex: 10, marginTop: 4, maxHeight: 200, overflowY: 'auto' }}>
                {results.map((z) => {
                  const isOwned = existingZipCodeIds.includes(z.id);
                  return (
                    <div
                      key={z.id}
                      onClick={() => {
                        if (!isOwned) {
                          setSelectedZip(z);
                          setZipForm('zip_code_id', z.id);
                          setQuery(`${z.code} - ${z.city}, ${z.state}`);
                          setResults([]);
                          clearZipErrors();
                        }
                      }}
                      style={{
                        padding: '8px 12px',
                        borderBottom: '1px solid var(--border-color)',
                        cursor: isOwned ? 'not-allowed' : 'pointer',
                        opacity: isOwned ? 0.5 : 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                      onMouseOver={(e: React.MouseEvent<HTMLDivElement>) => {
                        if (!isOwned) e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                      }}
                      onMouseOut={(e: React.MouseEvent<HTMLDivElement>) => {
                        if (!isOwned) e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <span>{z.code} - {z.city}, {z.state}</span>
                      {isOwned && <i className="fa-solid fa-check" style={{ color: 'var(--success)' }}></i>}
                    </div>
                  );
                })}
              </div>
            )}

            {selectedZip && (
              <div style={{ marginTop: 12, padding: 12, background: 'rgba(255,255,255,0.05)', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
                <i className="fa-solid fa-location-dot" style={{ color: 'var(--accent-1)', fontSize: '1.2rem' }}></i>
                <div>
                  <div style={{ fontWeight: 600 }}>{selectedZip.code}</div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-3)' }}>{selectedZip.city}, {selectedZip.state}</div>
                </div>
              </div>
            )}

            {zipErrors.zip_code_id && (
              <div role="alert" style={{ marginTop: 12, color: 'var(--danger)', fontSize: '0.9rem' }}>
                {String(zipErrors.zip_code_id)}
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!selectedZip || isAddingZip}
            onClick={claimSelectedZipCode}
          >
            <i className={`fa-solid ${isAddingZip ? 'fa-spinner fa-spin' : 'fa-plus'}`}></i>
            {isAddingZip ? ' Adding...' : ' Add Zipcode to Organization'}
          </button>
        </div>
      </div>
    </div>
  );
};


// 11. Main App
const App = () => {
  const {
    user,
    blasts: serverBlasts,
    leads: serverLeads,
    leadStatuses,
    activeTab: serverActiveTab,
    organizationId,
    organizations: serverOrganizations,
    global_organizations,
    active_organization,
    active_blast,
    flash
  } = usePage<DashboardPageProps>().props;

  // Use global_organizations for internal workspace scopes, reserving realCustomers for the Users view
  const organizations = global_organizations || [];

  const adminName = user ? user.email : "System Admin";
  const adminRole = user?.is_super_admin ? "Super Admin" : "Platform Admin";

  const activeTab = serverActiveTab || 'blasts';
  const [showNewBlastModal, setShowNewBlastModal] = useState(false);
  const [showNewOrganizationModal, setShowNewOrganizationModal] = useState(false);
  const [showAddZipcodeModal, setShowAddZipcodeModal] = useState(false);
  const [showNewTemplateModal, setShowNewTemplateModal] = useState(false);
  const [showNewCampaignModal, setShowNewCampaignModal] = useState(false);
  const [viewingCampaignId, setViewingCampaignId] = useState<number | null>(null);
  const [campaignSortBy, setCampaignSortBy] = useState<'createdAt' | 'updatedAt'>('createdAt');
  const [campaignFilter, setCampaignFilter] = useState<'active' | 'archived'>('active');
  const [editingTemplate, setEditingTemplate] = useState<DashboardTemplate | null>(null);
  const [editingOrganization, setEditingOrganization] = useState<EditableOrganization | null>(null);
  const [editingCampaignNicknameId, setEditingCampaignNicknameId] = useState<number | null>(null);
  const [editingCampaignNickname, setEditingCampaignNickname] = useState("");
  const [activeOrganizationId, setActiveOrganizationId] = useState<number | null>(() => {
    if (organizationId) return Number(organizationId);
    const saved = localStorage.getItem('activeOrganizationId');
    if (saved) return Number(saved);
    return organizations && organizations.length > 0 ? organizations[0].id : null;
  });

  useEffect(() => {
    if (organizationId) {
      setActiveOrganizationId(Number(organizationId));
      localStorage.setItem('activeOrganizationId', String(organizationId));
    }
  }, [organizationId]);

  useEffect(() => {
    if (flash?.new_organization_id) {
      setActiveOrganizationId(flash.new_organization_id);
      localStorage.setItem('activeOrganizationId', String(flash.new_organization_id));
    }
  }, [flash?.new_organization_id]);

  useEffect(() => {
    if (activeOrganizationId) {
      localStorage.setItem('activeOrganizationId', String(activeOrganizationId));
    }
  }, [activeOrganizationId]);

  const activeOrganization: DashboardOrganization = (organizations && organizations.find(c => c.id === Number(activeOrganizationId))) || { id: -1, name: 'No Organization', company: 'No Organization', templates: [], campaigns: [], zipCodes: [] };
  const viewingCampaign = viewingCampaignId ? activeOrganization.campaigns?.find(c => c.id === viewingCampaignId) : null;

  // Server props are read directly - never copied into useState. Mirroring a
  // prop into state (`useState(prop)` + `useEffect(() => setState(prop))`)
  // paints one frame with the OLD value before the effect swaps in the new
  // one, which is exactly the "flash" you see on every reload. Inertia already
  // owns this data; React just renders it.
  const blasts = serverBlasts || EMPTY_BLASTS;
  const leads = serverLeads || EMPTY_LEADS;

  // Local, optimistic edits to the `leads` prop go through Inertia's own
  // client-side prop update rather than a parallel React state copy.
  // replaceProp does a preserveState/preserveScroll client visit, so the page
  // component is not remounted and there is no second paint.
  // https://inertiajs.com/manual-visits#client-side-visits
  const setLeads = useCallback(
    (updater: DashboardLead[] | ((current: DashboardLead[]) => DashboardLead[])) => {
      router.replaceProp('leads', (current: DashboardLead[] | undefined) =>
        typeof updater === 'function' ? updater(current || []) : updater
      );
    },
    []
  );

  // Live Updates
  useEffect(() => {
    if (!active_organization && !activeOrganizationId) return;

    const orgId = active_organization?.id || activeOrganizationId;
    if (!orgId) return;

    const subscription = cableConsumer().subscriptions.create({ channel: 'InboxChannel', organization_id: orgId }, {
      received(data: any) {
        if (data.action === "new_message") {
          setLeads(currentLeads => {
            return currentLeads.map(l => {
              if (l.id === data.lead_id) {
                return {
                  ...l,
                  hasUnreadMessages: true,
                  messages: [...(l.messages || []), { ...data.message, dir: "in" }]
                };
              }
              return l;
            });
          });
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
    // Keyed on the resolved org id, not on the `active_organization` object -
    // an object prop gets a fresh identity on most responses and would tear
    // down and re-open the subscription on every navigation.
  }, [active_organization?.id, activeOrganizationId, setLeads]);

  const orgIdForLeadScoping = active_organization?.id || activeOrganizationId;
  // Count only rows that can actually appear as Inbox threads. A stale unread
  // flag on a lead with no messages otherwise creates a badge the operator can
  // never clear because InboxView intentionally excludes that row.
  const unreadCount = leads.filter(l =>
    l.hasUnreadMessages &&
    (l.messages?.length ?? 0) > 0 &&
    (!orgIdForLeadScoping || orgIdForLeadScoping < 0 || l.organizationId === orgIdForLeadScoping)
  ).length;

  const handleLaunchBlast = (data: LaunchBlastPayload) => {
    router.post('/blasts', {
      name: data.name,
      startDatetime: data.startDatetime,
      workingHoursStart: data.workingHoursStart,
      workingHoursEnd: data.workingHoursEnd,
      workingDays: data.workingDays,
      messageVolume: data.messageVolume,
      followUpDelayHours: data.followUpDelayHours,
      followUpTemplateId: data.followUpTemplateId,
      zipCode: data.zipCode,
      fromNumber: data.fromNumber,
      campaignId: data.campaignId,
      templateId: data.templateId,
      organizationId: data.organizationId || (active_organization || activeOrganization)?.id,
      testPhoneNumbers: data.testPhoneNumbers,
      reuseExistingLeads: data.reuseExistingLeads,
      contactLimit: data.contactLimit
    }, {
      onSuccess: () => setShowNewBlastModal(false)
    });
  };

  return (
    <div className="dashboard-root">
      <Sidebar adminName={adminName} adminRole={adminRole} activeTab={activeTab} isSuperAdmin={user?.is_super_admin} unreadCount={unreadCount} />

      <div className="main-content">
        <Topbar organizations={organizations || []} activeOrganization={active_organization || activeOrganization} onChangeOrganization={(val: string) => setActiveOrganizationId(Number(val))} />

        {showNewTemplateModal && (active_organization || activeOrganization) && (
          <NewTemplateModal
            onClose={() => setShowNewTemplateModal(false)}
            currentOrganization={active_organization || activeOrganization}
          />
        )}

        {editingTemplate && (active_organization || activeOrganization) && (
          <EditTemplateModal
            template={editingTemplate}
            onClose={() => setEditingTemplate(null)}
            currentOrganization={active_organization || activeOrganization}
            allOrganizations={serverOrganizations || organizations || []}
          />
        )}

        {activeTab === 'blasts' && (
          <BlastsView blasts={blasts} onNewBlast={() => setShowNewBlastModal(true)} organization={active_organization || activeOrganization} />
        )}

        {activeTab === 'blast_details' && active_blast && (
          <BlastDetailsView blast={active_blast} />
        )}

        {activeTab === 'inbox' && (
          <InboxView leads={leads} setLeads={setLeads} organization={active_organization || activeOrganization} />
        )}

        {activeTab === 'campaigns' && (
          <div className="view-area">
            <div className="page-header">
              <div>
                <h1 className="page-title">Campaigns</h1>
                <p className="page-subtitle">Manage 10DLC campaigns for {activeOrganization.company}</p>
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
                  <button
                    className="btn btn-sm"
                    style={{ borderRadius: 0, background: campaignFilter === 'active' ? 'var(--accent-1)' : 'transparent', color: campaignFilter === 'active' ? '#fff' : 'var(--text-2)', border: 'none' }}
                    onClick={() => setCampaignFilter('active')}
                  >
                    Active ({activeOrganization.campaigns.length})
                  </button>
                  <button
                    className="btn btn-sm"
                    style={{ borderRadius: 0, background: campaignFilter === 'archived' ? 'var(--accent-1)' : 'transparent', color: campaignFilter === 'archived' ? '#fff' : 'var(--text-2)', border: 'none' }}
                    onClick={() => setCampaignFilter('archived')}
                  >
                    Archived ({(activeOrganization.archivedCampaigns || []).length})
                  </button>
                </div>
                <select
                  className="form-control"
                  style={{ width: 'auto', height: '38px', padding: '0 12px', fontSize: '0.85rem' }}
                  value={campaignSortBy}
                  onChange={e => setCampaignSortBy(e.target.value as 'createdAt' | 'updatedAt')}
                >
                  <option value="createdAt">Sort: Created</option>
                  <option value="updatedAt">Sort: Updated</option>
                </select>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowNewCampaignModal(true)}
                >
                  <i className="fa-solid fa-plus"></i> New Campaign
                </button>
              </div>
            </div>
            <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
              {(campaignFilter === 'archived' ? (activeOrganization.archivedCampaigns || []) : activeOrganization.campaigns).slice().sort((a, b) => {
                const aTime = a[campaignSortBy] ? new Date(a[campaignSortBy] as string).getTime() : 0;
                const bTime = b[campaignSortBy] ? new Date(b[campaignSortBy] as string).getTime() : 0;
                return bTime - aTime;
              }).map((c) => (
                <div key={c.id} className="glass-panel" style={{ padding: 20, display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minWidth: 0, paddingRight: 16 }}>
                      <h3 style={{ fontSize: '1.1rem', fontWeight: 600, wordBreak: 'break-word', lineHeight: 1.3 }}>
                        {editingCampaignNicknameId === c.id ? (
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <input
                              type="text"
                              style={{
                                padding: '4px 8px',
                                fontSize: '0.9rem',
                                height: '34px',
                                background: 'rgba(255, 255, 255, 0.05)',
                                border: '1px solid var(--accent-1)',
                                color: '#fff',
                                borderRadius: '4px',
                                outline: 'none',
                                flex: '1 1 120px'
                              }}
                              value={editingCampaignNickname}
                              onChange={e => setEditingCampaignNickname(e.target.value)}
                              autoFocus
                            />
                            <button className="btn btn-primary" style={{ padding: '4px 12px', height: '34px' }} onClick={() => {
                              router.patch(`/campaigns/${c.id}`, { campaign: { nickname: editingCampaignNickname } }, {
                                preserveScroll: true,
                                onSuccess: () => setEditingCampaignNicknameId(null)
                              });
                            }}>Save</button>
                            <button className="btn btn-secondary" style={{ padding: '4px 12px', height: '34px' }} onClick={() => setEditingCampaignNicknameId(null)}>Cancel</button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                            {c.displayName}
                            <button className="btn-icon" style={{ fontSize: '0.85rem', color: 'var(--text-3)' }} onClick={() => {
                              setEditingCampaignNicknameId(c.id);
                              setEditingCampaignNickname(c.nickname || "");
                            }}><i className="fa-solid fa-pen-to-square"></i> Edit</button>
                          </div>
                        )}
                      </h3>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span className={`badge ${c.source === 'signal_house' ? 'badge-primary' : 'badge-warning'}`} style={{ fontSize: '0.7rem', padding: '2px 6px' }}>
                          {c.source === 'signal_house' ? 'SignalHouse' : c.source === 'signal_mash' ? 'SignalMash' : 'Unknown Source'}
                        </span>
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-3)', fontFamily: 'monospace' }}>
                          {c.source_id}
                        </span>
                      </div>
                    </div>
                    <span className={`badge ${campaignStatusBadge(c.status).className}`} style={{ height: 'fit-content', whiteSpace: 'nowrap' }}>{campaignStatusBadge(c.status).label}</span>
                  </div>
                  {c.description && (
                    <p style={{ color: 'var(--text-3)', fontSize: '0.8rem', lineHeight: 1.4, marginBottom: 16, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {c.description}
                    </p>
                  )}
                  <div style={{ color: 'var(--text-3)', fontSize: '0.85rem', marginBottom: 12 }}>Sending Numbers ({c.fromNumbers.length})</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                    {c.fromNumbers.map((num) => (
                      <div key={num} style={{ background: 'rgba(255,255,255,0.05)', padding: '4px 8px', borderRadius: 4, fontSize: '0.8rem', border: '1px solid var(--border-color)' }}>
                        {num}
                      </div>
                    ))}
                  </div>

                  <div style={{ display: 'flex', gap: 8, marginTop: 'auto', paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                    <button className="btn btn-secondary btn-sm" style={{ flex: 1, minWidth: 0, justifyContent: 'center', whiteSpace: 'nowrap' }} onClick={() => setViewingCampaignId(c.id)}>
                      <i className="fa-solid fa-eye"></i> View
                    </button>
                    {c.archivedAt ? (
                      <Link
                        href={`/campaigns/${c.id}/unarchive`}
                        method="patch"
                        as="button"
                        className="btn btn-secondary btn-sm"
                        style={{ flex: 1, minWidth: 0, justifyContent: 'center', whiteSpace: 'nowrap' }}
                        preserveScroll
                      >
                        <i className="fa-solid fa-box-open"></i> Unarchive
                      </Link>
                    ) : (
                      <>
                        <Link
                          href={`/campaigns/${c.id}/sync`}
                          method="post"
                          as="button"
                          className="btn btn-secondary btn-sm"
                          style={{ flex: 1, minWidth: 0, justifyContent: 'center', whiteSpace: 'nowrap' }}
                          preserveScroll
                        >
                          <i className="fa-solid fa-rotate"></i> Sync
                        </Link>
                        <Link
                          href={`/campaigns/${c.id}/archive`}
                          method="patch"
                          as="button"
                          className="btn btn-danger btn-sm"
                          style={{ flex: 1, minWidth: 0, justifyContent: 'center', whiteSpace: 'nowrap' }}
                          preserveScroll
                        >
                          <i className="fa-solid fa-box-archive"></i> Archive
                        </Link>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'templates' && (
          <div className="view-area">
            <div className="page-header">
              <div>
                <h1 className="page-title">Templates</h1>
                <p className="page-subtitle">Manage message templates for {(active_organization?.name || activeOrganization?.company)}</p>
              </div>
              <button onClick={() => setShowNewTemplateModal(true)} className="btn btn-primary"><i className="fa-solid fa-plus"></i> New Template</button>
            </div>
            <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
              {(active_organization?.templates || activeOrganization?.templates || []).map((t) => {
                const campaignsToSearch = active_organization?.campaigns || activeOrganization?.campaigns || [];
                const linkedCampaign = campaignsToSearch.find((c) => c.id === t.campaignId);
                return (
                  <div key={t.id} className="glass-panel" style={{ padding: 20, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                      <h3 style={{ fontSize: '1.1rem', fontWeight: 600 }}>{t.name}</h3>
                    </div>
                    <div style={{ background: 'rgba(0,0,0,0.2)', padding: 12, borderRadius: 8, fontStyle: 'italic', fontSize: '0.9rem', color: 'var(--text-2)', marginBottom: 16, flex: 1 }}>
                      "{t.body}"
                    </div>
                    <div style={{ borderTop: '1px solid var(--glass-border)', paddingTop: 12, fontSize: '0.8rem', color: 'var(--text-3)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span><i className="fa-solid fa-link" style={{ marginRight: 6 }}></i> {linkedCampaign ? linkedCampaign.displayName : 'Unlinked'}</span>
                      <button
                        className="btn btn-secondary"
                        onClick={() => setEditingTemplate(t)}
                        style={{ padding: '6px 10px', fontSize: '0.8rem' }}
                      >
                        <i className="fa-solid fa-pen-to-square"></i> Edit
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'leads' && (
          <LeadsView leads={leads} leadStatuses={leadStatuses || []} organization={active_organization || activeOrganization} />
        )}



        {activeTab === 'organizations' && (
          <OrganizationsView
            organizations={serverOrganizations || []}
            activeOrganizationId={activeOrganizationId}
            onNew={() => setShowNewOrganizationModal(true)}
            onSwitch={(id) => setActiveOrganizationId(id)}
            onEdit={(org) => setEditingOrganization(org)}
          />
        )}

        {activeTab === 'organization_details' && active_organization && (
          <div className="view-area" style={{ animation: 'fadeIn 0.3s ease' }}>
            <div className="page-header" style={{ marginBottom: 24 }}>
              <div>
                <h1 className="page-title">{active_organization.name} Details</h1>
                <p className="page-subtitle">Manage settings and configurations for this organization.</p>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button className="btn btn-primary" onClick={() => setEditingOrganization(active_organization)}>
                  <i className="fa-solid fa-pen-to-square"></i> Edit
                </button>
                <button
                  className="btn btn-secondary"
                  style={{ borderColor: 'rgba(255, 60, 60, 0.4)', color: 'var(--danger)' }}
                  onClick={() => {
                    if (confirm(`Are you sure you want to delete "${active_organization.name}"? This cannot be undone.`)) {
                      router.delete(`/organizations/${active_organization.id}`)
                    }
                  }}
                >
                  <i className="fa-solid fa-trash"></i> Delete
                </button>
                <Link href="/organizations" className="btn btn-secondary" style={{ textDecoration: 'none' }}><i className="fa-solid fa-arrow-left"></i> Back to Organizations</Link>
              </div>
            </div>

            <div className="glass-panel" style={{ padding: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
                <div style={{ width: 64, height: 64, borderRadius: 12, background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem' }}>
                  <i className="fa-solid fa-sitemap" style={{ color: 'var(--accent-1)' }}></i>
                </div>
                <div>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: 600 }}>{active_organization.name}</h2>
                  <div style={{ color: 'var(--text-2)' }}>Status: <span className="badge badge-success">{active_organization.status || 'Active'}</span></div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                <div className="glass-panel" style={{ padding: 20, background: 'rgba(0,0,0,0.2)' }}>
                  <h3 style={{ marginBottom: 16, fontSize: '1.1rem', color: 'var(--accent-1)' }}>Campaign Configuration</h3>
                  <p style={{ color: 'var(--text-2)', fontSize: '0.9rem', marginBottom: 12 }}>{active_organization.campaigns?.filter(c => !c.archivedAt).length || 0} Active 10DLC Campaigns</p>
                  <Link href="/campaigns" className="btn btn-secondary" style={{ width: '100%', textAlign: 'center', display: 'block', textDecoration: 'none' }}>Manage Campaigns</Link>
                </div>
                <div className="glass-panel" style={{ padding: 20, background: 'rgba(0,0,0,0.2)' }}>
                  <h3 style={{ marginBottom: 16, fontSize: '1.1rem', color: 'var(--accent-1)' }}>Usage Settings</h3>
                  <p style={{ color: 'var(--text-2)', fontSize: '0.9rem', marginBottom: 12 }}>{active_organization.templates?.length || 0} SMS Templates Configured</p>
                  <Link href="/templates" className="btn btn-secondary" style={{ width: '100%', textAlign: 'center', display: 'block', textDecoration: 'none' }}>Manage Templates</Link>
                </div>
              </div>

              {/* Zip Code Management */}
              {active_organization.industry ? (
                <div className="glass-panel" style={{ marginTop: 24, padding: 24, background: 'rgba(0,0,0,0.2)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                    <div>
                      <h3 style={{ fontSize: '1.25rem', color: 'var(--accent-1)', marginBottom: 4 }}>Claimed Zip Codes</h3>
                      <p style={{ color: 'var(--text-2)', fontSize: '0.9rem' }}>
                        Your organization operates in the <strong>{active_organization.industry}</strong> industry.
                        You have exclusive rights to the {active_organization.zipCodes?.length || 0} Zip Codes listed here.
                      </p>
                    </div>

                    <div style={{ position: 'relative' }}>
                      <button className="btn btn-primary" onClick={() => setShowAddZipcodeModal(true)}>
                        <i className="fa-solid fa-plus"></i> Add Zipcode
                      </button>
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {active_organization.zipCodes?.map((z) => (
                      <div key={z.id} className="glass-panel" style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(0,0,0,0.1)' }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '1.1rem', color: 'var(--text-1)' }}>{z.code}</div>
                          <div style={{ color: 'var(--text-3)', fontSize: '0.9rem' }}>{z.city}, {z.state}</div>
                          {z.propertyCount != null && (
                            <div style={{ fontSize: '0.8rem', color: 'var(--accent-1)', marginTop: 2 }}>
                              <i className="fa-solid fa-house" style={{ marginRight: 4 }}></i>
                              {z.propertyCount.toLocaleString()} properties
                            </div>
                          )}
                        </div>
                        <button
                          className="btn btn-secondary"
                          style={{ borderColor: 'rgba(255, 60, 60, 0.4)', color: 'var(--danger)', fontSize: '0.85rem', padding: '6px 12px' }}
                          onClick={() => {
                            if (confirm(`Are you sure you want to release Zip Code ${z.code}? Another organization in the ${active_organization.industry} industry could claim it immediately.`)) {
                              const updatedIds = active_organization.zipCodes.filter((zc) => zc.id !== z.id).map((x) => x.id);
                              router.patch(`/organizations/${active_organization.id}`, {
                                organization: { zip_code_ids: updatedIds }
                              }, { preserveScroll: true });
                            }
                          }}
                        >
                          <i className="fa-solid fa-trash-can" style={{ marginRight: 6 }}></i> Unassign
                        </button>
                      </div>
                    ))}
                    {(!active_organization.zipCodes || active_organization.zipCodes.length === 0) && (
                      <div style={{ color: 'var(--text-3)', fontStyle: 'italic', padding: '12px 0' }}>No Zip Codes claimed yet. Click 'Add Zipcode' above to reserve one.</div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="glass-panel" style={{ marginTop: 24, padding: 24, background: 'rgba(255, 60, 60, 0.1)', border: '1px solid rgba(255, 60, 60, 0.3)' }}>
                  <h3 style={{ fontSize: '1.1rem', color: 'var(--danger)', marginBottom: 8 }}><i className="fa-solid fa-triangle-exclamation"></i> Industry Required for Zip Codes</h3>
                  <p style={{ color: 'var(--text-2)', fontSize: '0.9rem' }}>
                    You must assign an Industry to this Organization by editing its settings before you can claim exclusive Zip Codes.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {showNewBlastModal && (
        <NewBlastModal
          onClose={() => setShowNewBlastModal(false)}
          onLaunch={handleLaunchBlast}
          organization={active_organization || activeOrganization}
        />
      )}

      {showNewCampaignModal && (active_organization || activeOrganization) && (
        <NewCampaignModal
          onClose={() => setShowNewCampaignModal(false)}
          currentOrganization={active_organization || activeOrganization}
        />
      )}

      {showNewOrganizationModal && (
        <NewOrganizationModal onClose={() => setShowNewOrganizationModal(false)} />
      )}

      {showAddZipcodeModal && (active_organization || activeOrganization) && (
        <AddZipcodeModal
          organization={active_organization || activeOrganization}
          existingZipCodeIds={((active_organization || activeOrganization).zipCodes || []).map((z) => z.id)}
          onClose={() => setShowAddZipcodeModal(false)}
        />
      )}

      {editingOrganization && (
        <EditOrganizationModal
          organization={editingOrganization}
          onClose={() => setEditingOrganization(null)}
        />
      )}

      {viewingCampaign && (
        <ViewCampaignModal
          campaign={viewingCampaign}
          onClose={() => setViewingCampaignId(null)}
        />
      )}
    </div>
  );
};

export default function DashboardIndex() {
  return (
    <>
      <Head title="Dashboard" />
      <App />
    </>
  )
}
